<template>
	<div>
		<div v-if="isMobile" class="mobile-login-screen">
			<!-- Top header area with wave background -->
			<div
				class="mobile-header-panel"
				:class="{ 'business-header-active': userType === 'admin' }"
			>
				<div v-if="!isRoute" class="header-back-wrapper">
					<img
						src="@/assets/img/homepage/round-right-iconB.svg"
						class="header-back-icon"
						alt="Back"
						@click="$emit('close')"
					/>
				</div>

				<div class="logo-text-wrapper text-center">
					<!-- hire3xround-logo -->
					<img
						src="@/assets/img/homepage/hire3x-round-logo.svg"
						class="hire3x-round-logo mb-3"
						alt="Logo"
					/>

					<h2
						class="mobile-header-title"
						:class="{ 'business-title': userType === 'admin' }"
					>
						{{
							userType === "admin"
								? "Smarter screening. Faster hiring."
								: "Don’t Just Work. Build a Career You Love."
						}}
					</h2>
					<p class="mobile-header-subtitle">
						Sign up to find the next big opportunity
					</p>
				</div>
			</div>

			<!-- Bottom Card Panel -->
			<b-overlay
				:show="show"
				:opacity="overlayOpacity"
				rounded="sm"
				class="mobile-overlay-wrapper"
			>
				<template #overlay>
					<div class="overlaybox">
						<b-spinner class="login-loader"></b-spinner>
						<span class="overlaytxt">{{ loaderLabel }}</span>
					</div>
				</template>

				<div
					class="mobile-form-panel"
					:class="{ 'set-password-padding': setPasswordMode }"
				>
					<template v-if="!setPasswordMode">
						<!-- Candidate / Business Segment Control -->
						<div class="mob-toggle-container mb-4">
							<div class="mob-toggle-track d-flex w-100">
								<button
									class="mob-toggle-btn flex-fill"
									:class="{ active: userType === 'candidate' }"
									@click="userType = 'candidate'"
								>
									Candidate
								</button>
								<!-- <button
									class="mob-toggle-btn flex-fill"
									:class="{ active: userType === 'admin' }"
									@click="userType = 'admin'"
								>
									Business
								</button> -->
							</div>
						</div>

						<!-- Google SSO (Candidate only) -->
						<template v-if="userType === 'candidate'">
							<!-- Google SSO (Candidate only) -->
							<div class="mb-4">
								<b-button
									variant="outline-light"
									block
									class="mob-google-btn"
									@click="googleLogin('newlogin')"
								>
									<img
										src="@/assets/img/homepage/google-up.svg"
										height="20"
										class="mr-2"
										alt="Google"
									/>
									Login with Google
								</b-button>
								<div
									class="invalid-msg-txt mt-2"
									v-if="invalidCredential && !otpSent && !setPasswordMode"
								>
									{{ invalidText }}
								</div>
							</div>

							<div class="mob-separator mb-4">
								<span>Sign up / Login with</span>
							</div>

							<!-- Input Field Container -->
							<div class="mb-4">
								<!-- Identifier input (phone or email) -->
								<div>
									<!-- Phone Number Input -->
									<div v-if="!useEmail">
										<div class="input-field-container">
											<b-form-input
												ref="phoneInput"
												id="candidatePhone"
												type="tel"
												v-model.trim="loginPhone.number"
												autocomplete="off"
												maxlength="11"
												@keypress.enter="handleSubmit()"
												@keypress="otpNumbersOnly"
												:class="{
													inputError: loginPhoneInvalid && !otpSent,
												}"
												placeholder="Phone Number"
												class="phone-input w-100 no-number"
												:disabled="otpSent"
											></b-form-input>
											<span
												v-if="otpSent"
												class="phone-edit-action"
												@click="handleEdit()"
											>
												Edit
											</span>
										</div>
										<div
											class="invalid-msg-txt mt-1"
											v-if="loginPhoneInvalid && !otpSent"
										>
											Invalid Phone Number
										</div>
										<div
											class="invalid-msg-txt mt-1"
											v-if="sendOtpError && !useEmail && !otpExceeded"
										>
											{{ sendOtpError }}
										</div>

										<div v-if="!otpSent" class="text-right mt-2">
											<a
												href="javascript:void(0)"
												@click="toggleLoginMode(true)"
												class="mob-toggle-link"
											>
												Use E-mail ID instead
											</a>
										</div>
									</div>

									<!-- Email Input -->
									<div v-else>
										<div class="position-relative">
											<b-form-input
												v-model="$v.email.$model"
												@keydown.space.prevent
												ref="candmail"
												@keypress.enter="handleSubmit()"
												@input="sendOtpError = ''"
												type="email"
												placeholder="Enter Email ID"
												class="single-field-update"
												:class="{
													error:
														$v.email.$error || (sendOtpError && !otpExceeded),
												}"
												:disabled="otpSent"
											></b-form-input>
											<a
												v-if="otpSent"
												href="javascript:void(0)"
												@click="handleEdit()"
												class="dummy-edit-btn mob-email-edit-overlay"
												>Edit</a
											>
										</div>
										<div
											class="invalid-msg-txt mt-1"
											v-if="$v.email.$error && !otpSent"
										>
											{{
												$v.email.$dirty && !$v.email.required
													? "Required"
													: $v.email.required &&
													  (!$v.email.email || !$v.email.alphaEmail)
													? "Invalid Email"
													: !$v.email.maxLength
													? "Should not execed 64 characters"
													: ""
											}}
										</div>
										<div
											class="invalid-msg-txt mt-1"
											v-if="sendOtpError && useEmail && !otpExceeded"
										>
											{{ sendOtpError }}
										</div>

										<div v-if="!otpSent" class="text-right mt-2">
											<a
												href="javascript:void(0)"
												@click="toggleLoginMode(false)"
												class="mob-toggle-link"
											>
												Use Phone Number instead
											</a>
										</div>
									</div>
								</div>

								<!-- Case 2: OTP has been sent (show OTP input / Password input) -->
								<div v-if="otpSent">
									<!-- Enter OTP Field -->
									<div v-if="useOtp">
										<b-form-input
											v-model.trim="otpCode"
											placeholder="Enter OTP"
											class="single-field-update mt-3"
											maxlength="6"
											:class="{
												'error-border': $v.otpCode.$error || invalidCredential,
											}"
											@keypress="otpNumbersOnly"
										></b-form-input>
										<div
											class="invalid-msg-txt mt-1"
											v-if="$v.otpCode.$error && !$v.otpCode.required"
										>
											Required
										</div>
										<div
											class="invalid-msg-txt mt-1"
											v-else-if="$v.otpCode.$error"
										>
											Please enter a valid OTP
										</div>
										<div
											class="invalid-msg-txt mt-1"
											v-else-if="invalidCredential && !setPasswordMode"
										>
											{{ invalidText }}
										</div>

										<!-- Links under Enter OTP -->
										<div
											class="d-flex justify-content-between align-items-center mt-3"
											style="
												font-family: 'Inter', sans-serif !important;
												font-size: 12px !important;
											"
										>
											<!-- Left side link -->
											<a
												v-if="userExists === false"
												href="javascript:void(0)"
												@click="openPasswordSetup()"
												style="
													color: #4a7eff !important;
													font-weight: 500 !important;
													text-decoration: underline !important;
												"
											>
												Set password to sign up
											</a>
											<a
												v-else-if="userExists === true"
												href="javascript:void(0)"
												@click="usePasswordOrSetup()"
												style="
													color: #4a7eff !important;
													font-weight: 500 !important;
													text-decoration: underline !important;
												"
											>
												Use password
											</a>
											<span v-else></span>

											<!-- Right Timer / Resend Link -->
											<span
												v-if="timerOut"
												style="
													color: #4a7eff !important;
													font-weight: 500 !important;
												"
											>
												{{
													otpExceeded
														? "OTP already sent. Resend in"
														: "Resend in"
												}}
												<span style="font-weight: 600 !important">{{
													formattedTime
												}}</span>
											</span>
											<a
												v-else
												href="javascript:void(0)"
												@click="register('resend')"
												style="
													color: #4a7eff !important;
													font-weight: 500 !important;
													text-decoration: underline !important;
												"
											>
												Resend OTP
											</a>
										</div>
									</div>

									<!-- Enter Password Field -->
									<div v-else>
										<div class="position-relative mt-3">
											<b-form-input
												v-model="$v.password.$model"
												:type="showPassword ? 'text' : 'password'"
												placeholder="Enter Password"
												class="single-field-update"
												:class="{ error: $v.password.$error }"
											></b-form-input>
											<span
												class="password-toggle-icon"
												@click="showPassword = !showPassword"
											>
												<svg
													width="18"
													height="18"
													viewBox="0 0 24 24"
													fill="none"
													v-if="!showPassword"
												>
													<path
														d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
														stroke="#999"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
													<circle
														cx="12"
														cy="12"
														r="3"
														stroke="#999"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
												</svg>
												<svg
													width="18"
													height="18"
													viewBox="0 0 24 24"
													fill="none"
													v-else
												>
													<path
														d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"
														stroke="#999"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
												</svg>
											</span>
										</div>

										<div class="invalid-msg-txt mt-1" v-if="$v.password.$error">
											{{
												$v.password.$dirty && !$v.password.required
													? "Required"
													: !$v.password.maxLength && $v.password.required
													? "Should not execed 55 characters"
													: ""
											}}
										</div>
										<div
											class="invalid-msg-txt mt-1"
											v-else-if="invalidCredential"
										>
											{{ invalidText }}
										</div>

										<!-- Links under Enter Password -->
										<div
											class="d-flex justify-content-between align-items-center mt-3"
											style="
												font-family: 'Inter', sans-serif !important;
												font-size: 12px !important;
											"
										>
											<a
												href="javascript:void(0)"
												@click="useOtpLogin()"
												style="
													color: #4a7eff !important;
													font-weight: 600 !important;
													text-decoration: underline !important;
												"
											>
												Login with OTP
											</a>
											<a
												href="javascript:void(0)"
												@click.stop.prevent="openPasswordSetup(true)"
												style="
													color: #333333 !important;
													font-weight: 500 !important;
													text-decoration: none !important;
												"
											>
												Forgot Password?
											</a>
										</div>
									</div>
								</div>
							</div>

							<!-- Continue Button -->
							<b-button
								variant="primary"
								@click="handleSubmit()"
								block
								class="mob-continue-btn mb-4"
								:disabled="
									userType === 'candidate' &&
									useOtp &&
									!otpSent &&
									(useEmail
										? !email || email.trim() === ''
										: !loginPhone.number || loginPhone.number.trim() === '')
								"
							>
								{{
									userType === "candidate" && useOtp
										? !otpSent
											? "Continue with OTP"
											: userExists === false
											? "Sign up"
											: "Login"
										: "Login"
								}}
							</b-button>

							<!-- Terms Disclaimer -->
							<div class="mob-terms-container text-center">
								<p class="mob-terms-text">
									By Continuing, you accept to the
									<a
										href="javascript:void(0)"
										@click="terms()"
										class="mob-terms-link"
										>Terms and Conditions</a
									>
									and
									<a
										href="javascript:void(0)"
										@click="goToPrivacyPolicy()"
										class="mob-terms-link"
										>Privacy Policy</a
									>
									of Hire3x.com
								</p>
							</div>
						</template>

						<!-- business section mobile -->
						<template v-else-if="userType === 'admin'">
							<!-- login business mobile -->
							<template v-if="mode === 'login'">
								<div class="mb-3">
									<b-form-input
										v-model="$v.email.$model"
										@keydown.space.prevent
										ref="candmail"
										@keypress.enter="handleSubmit()"
										type="email"
										placeholder="Email"
										class="single-field-update"
										:class="{ error: $v.email.$error }"
									></b-form-input>
									<div class="invalid-msg-txt mt-1" v-if="$v.email.$error">
										{{
											$v.email.$dirty && !$v.email.required
												? "Required"
												: $v.email.required &&
												  (!$v.email.email || !$v.email.alphaEmail)
												? "Invalid Email"
												: !$v.email.maxLength
												? "Should not execed 64 characters"
												: ""
										}}
									</div>
								</div>
								<div class="mb-3">
									<div class="position-relative">
										<b-form-input
											v-model="$v.password.$model"
											:type="showPassword ? 'text' : 'password'"
											placeholder="Password"
											class="single-field-update password-input"
											ref="candpass"
											@keypress.enter="handleSubmit()"
											autocomplete="off"
											:class="{ error: $v.password.$error }"
										></b-form-input>
										<span
											class="password-toggle-icon"
											@click="showPassword = !showPassword"
										>
											<svg
												width="18"
												height="18"
												viewBox="0 0 24 24"
												fill="none"
												v-if="!showPassword"
											>
												<path
													d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
													stroke="#999"
													stroke-width="2"
													stroke-linecap="round"
													stroke-linejoin="round"
												/>
												<circle
													cx="12"
													cy="12"
													r="3"
													stroke="#999"
													stroke-width="2"
													stroke-linecap="round"
													stroke-linejoin="round"
												/>
											</svg>
											<svg
												width="18"
												height="18"
												viewBox="0 0 24 24"
												fill="none"
												v-else
											>
												<path
													d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"
													stroke="#999"
													stroke-width="2"
													stroke-linecap="round"
													stroke-linejoin="round"
												/>
											</svg>
										</span>
									</div>
									<div class="invalid-msg-txt mt-1" v-if="$v.password.$error">
										{{
											$v.password.$dirty && !$v.password.required
												? "Required"
												: !$v.password.maxLength && $v.password.required
												? "Should not execed 55 characters"
												: ""
										}}
									</div>

									<div
										class="invalid-msg-txt mt-1"
										v-else-if="invalidCredential"
									>
										{{ invalidText }}
									</div>
								</div>
								<div class="text-left mb-4">
									<a
										href="javascript:void(0)"
										class="forgot-link"
										@click="mode = 'forgot'"
										>Forgot your password?</a
									>
								</div>

								<b-button
									variant="primary"
									@click="handleSubmit()"
									block
									class="login-submit-btn mb-3"
								>
									Login
								</b-button>
								<div class="text-center">
									<p class="signup-text font-inter">
										Don't have an account yet?
										<a @click="goToDemo()" class="register-link pointer"
											>Request Demo</a
										>
									</p>
								</div>
							</template>
							<!-- Forgot Password View Mobile -->
							<div v-if="mode === 'forgot'" class="forgot d-flex flex-column">
								<b-overlay
									:show="show"
									:opacity="overlayOpacity"
									rounded="sm"
									class="h-100"
								>
									<template #overlay>
										<div class="overlaybox">
											<b-spinner class="login-loader"></b-spinner>
											<span class="overlaytxt">Loading</span>
										</div>
									</template>
									<h3 class="font-inter fw-600 color-darkblack mb-2 fs-22 mt-4">
										Forgot Passwords?
									</h3>
									<p class="font-inter color-gray mb-4 fs-14">
										Enter the email address associated with this account.
									</p>

									<div class="mb-4">
										<b-form-input
											type="email"
											placeholder="Email"
											class="login-input"
											v-model="$v.email.$model"
											@keydown.space.prevent
											ref="candmail"
											@keypress.enter="forgotPassword()"
											:class="{ error: $v.email.$error || invalidCredential }"
										></b-form-input>
										<div
											class="invalid-msg-txt"
											v-if="$v.email.$error || invalidCredential"
										>
											{{
												$v.email.$dirty && !$v.email.required
													? "Required"
													: $v.email.required &&
													  (!$v.email.email || !$v.email.alphaEmail)
													? "Invalid Email"
													: !$v.email.maxLength
													? "Should not execed 64 characters"
													: invalidCredential
													? invalidText
													: ""
											}}
										</div>
									</div>

									<b-button
										@click="forgotPassword()"
										variant="primary"
										block
										class="login-submit-btn mb-4"
									>
										Send OTP
									</b-button>

									<div class="text-center mt-2">
										<a
											href="javascript:void(0)"
											@click="mode = 'login'"
											class="back-to-login font-inter fw-500 color-blue text-decoration-none"
										>
											<i class="fas fa-arrow-left mr-1"></i> Back to login
										</a>
									</div>
								</b-overlay>
							</div>

							<!-- Verify OTP View Mobile -->
							<div
								v-if="
									(mode === 'otp' || mode === 'forgotOtp') &&
									userType !== 'candidate'
								"
								class="forgotOtp d-flex flex-column"
							>
								<b-overlay
									:show="show"
									:opacity="overlayOpacity"
									rounded="sm"
									class="h-100"
								>
									<template #overlay>
										<div class="overlaybox">
											<b-spinner class="login-loader"></b-spinner>
											<span class="overlaytxt">Loading</span>
										</div>
									</template>
									<hr
										id="hideinmobile"
										style="border-top: 1px solid #dee2e6"
										class="mb-4 mt-4"
									/>
									<h3 class="font-inter fw-600 color-darkblack mb-2 fs-22">
										Verify OTP
									</h3>
									<p class="font-inter color-gray mb-4 fs-14">
										Please enter the 6 digits OTP sent to :-
										<a
											href="javascript:void(0)"
											class="color-blue text-decoration-none"
										>
											{{ formatEmail }}</a
										>
									</p>

									<div
										class="d-flex otp-input-row justify-content-between"
										:class="
											invalidCredential ||
											($v.otpDigits.$dirty &&
												$v.otpDigits.$invalid &&
												verifyotp)
												? 'mb-1'
												: 'mb-4'
										"
									>
										<input
											ref="firstInput"
											type="number"
											:class="{
												error:
													invalidCredential ||
													($v.otpDigits.num1.$error && verifyotp),
											}"
											maxlength="1"
											placeholder="0"
											@keydown="moveToNextField($event, 'secondInput', null)"
											@keypress="restrictToOneDigit"
											class="otp-box no-number"
											v-model="$v.otpDigits.num1.$model"
										/>
										<input
											ref="secondInput"
											type="number"
											:class="{
												error:
													invalidCredential ||
													($v.otpDigits.num2.$error && verifyotp),
											}"
											@keydown="
												moveToNextField($event, 'thirdInput', 'firstInput')
											"
											@keypress="restrictToOneDigit"
											@paste="preventPaste"
											placeholder="0"
											maxlength="1"
											class="otp-box no-number"
											v-model="$v.otpDigits.num2.$model"
										/>
										<input
											ref="thirdInput"
											type="number"
											:class="{
												error:
													invalidCredential ||
													($v.otpDigits.num3.$error && verifyotp),
											}"
											@keydown="
												moveToNextField($event, 'fourthInput', 'secondInput')
											"
											@keypress="restrictToOneDigit"
											@paste="preventPaste"
											placeholder="0"
											maxlength="1"
											class="otp-box no-number"
											v-model="$v.otpDigits.num3.$model"
										/>
										<input
											ref="fourthInput"
											type="number"
											:class="{
												error:
													invalidCredential ||
													($v.otpDigits.num4.$error && verifyotp),
											}"
											@keydown="
												moveToNextField($event, 'fifthInput', 'thirdInput')
											"
											@keypress="restrictToOneDigit"
											@paste="preventPaste"
											placeholder="0"
											maxlength="1"
											class="otp-box no-number"
											v-model="$v.otpDigits.num4.$model"
										/>
										<input
											ref="fifthInput"
											type="number"
											:class="{
												error:
													invalidCredential ||
													($v.otpDigits.num5.$error && verifyotp),
											}"
											@keydown="
												moveToNextField($event, 'sixthInput', 'fourthInput')
											"
											@keypress="restrictToOneDigit"
											@paste="preventPaste"
											placeholder="0"
											maxlength="1"
											class="otp-box no-number"
											v-model="$v.otpDigits.num5.$model"
										/>
										<input
											ref="sixthInput"
											type="number"
											:class="{
												error:
													invalidCredential ||
													($v.otpDigits.num6.$error && verifyotp),
											}"
											@keypress="restrictToOneDigit"
											@keypress.enter="
												mode === 'otp' ? handleSubmit() : resetPasswordOTP()
											"
											@keydown="moveToNextField($event, null, 'fifthInput')"
											@paste="preventPaste"
											placeholder="0"
											maxlength="1"
											class="otp-box no-number"
											v-model="$v.otpDigits.num6.$model"
										/>
									</div>
									<div class="invalid-msg-txt" v-if="invalidCredential">
										{{ invalidText }}
									</div>
									<div
										class="invalid-msg-txt"
										v-else-if="
											$v.otpDigits.$dirty && $v.otpDigits.$invalid && verifyotp
										"
									>
										Enter a valid otp
									</div>
									<template v-if="!otpExceeded">
										<div class="text-left mb-4">
											<span class="font-inter fs-13 color-gray"
												>Didn't receive code?
											</span>
											<span v-if="timerOut" class="font-inter fs-13 color-gray">
												Resend in
												<span
													v-if="timerOut"
													class="fw-500 fs-13 text-decoration-none"
													style="color: red"
													>{{ formattedTime }}</span
												>
											</span>
											<a
												v-else
												href="javascript:void(0)"
												@click="
													mode === 'otp'
														? register('resend')
														: forgotPassword('resend')
												"
												class="color-blue fw-500 text-decoration-none"
												>Resend OTP</a
											>
										</div>
									</template>
									<div v-else class="text-left mb-4">
										<span class="fam-inter fs-13 color-gray"
											>OTP has already been sent.<br v-if="isMobile" />
											You can request a new OTP in
										</span>
										<span
											class="fw-500 fs-13 text-decoration-none"
											style="color: red"
											>{{ formattedTime }}</span
										>
									</div>
									<b-button
										@click="
											mode === 'otp' ? handleSubmit() : resetPasswordOTP()
										"
										variant="primary"
										block
										class="login-submit-btn mb-4"
									>
										Verify
									</b-button>

									<div class="text-center mt-2">
										<a
											href="javascript:void(0)"
											@click="mode = 'login'"
											class="back-to-login font-inter fw-500 color-blue text-decoration-none"
										>
											<i class="fas fa-arrow-left mr-1"></i> Back to login
										</a>
									</div>
								</b-overlay>
							</div>

							<!-- Reset Password View Mobile -->
							<div v-if="mode === 'reset'" class="reset d-flex flex-column">
								<b-overlay
									:show="show"
									:opacity="overlayOpacity"
									rounded="sm"
									class="h-100"
								>
									<template #overlay>
										<div class="overlaybox">
											<b-spinner class="login-loader"></b-spinner>
											<span class="overlaytxt">Loading</span>
										</div>
									</template>
									<hr
										id="hideinmobile"
										style="border-top: 1px solid #dee2e6"
										class="mb-4 mt-4"
									/>
									<h3 class="font-inter fw-600 color-darkblack mb-2 fs-22">
										Reset Password
									</h3>
									<p class="font-inter color-gray mb-4 fs-14">
										Please enter the new password to reset your password.
									</p>
									<div class="mb-3">
										<div class="position-relative">
											<b-form-input
												:type="showPass ? 'text' : 'password'"
												@input="validatePassword('rePassword')"
												v-model="$v.rePassword.$model"
												ref="passnew"
												placeholder="New Password"
												class="login-input pr-5"
												@keypress.enter="resetPassword()"
												:class="{
													error: $v.rePassword.$error || checkpassowrdInvalid,
												}"
											></b-form-input>
											<span
												class="password-toggle-icon"
												@click="showPass = !showPass"
											>
												<svg
													width="18"
													height="18"
													viewBox="0 0 24 24"
													fill="none"
													v-if="!showPass"
												>
													<path
														d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
														stroke="#999"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
													<circle
														cx="12"
														cy="12"
														r="3"
														stroke="#999"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
												</svg>
												<svg
													width="18"
													height="18"
													viewBox="0 0 24 24"
													fill="none"
													v-else
												>
													<path
														d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"
														stroke="#999"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
												</svg>
											</span>
										</div>
										<div
											class="invalid-msg-txt"
											v-if="$v.rePassword.$error || checkpassowrdInvalid"
										>
											{{
												$v.rePassword.$dirty && !$v.rePassword.required
													? "Required"
													: checkpassowrdInvalid
													? passwordValidation
													: !$v.rePassword.maxLength && $v.rePassword.required
													? "Should not exceed 55 characters"
													: ""
											}}
										</div>
									</div>
									<div class="mb-4">
										<div class="position-relative">
											<b-form-input
												type="password"
												@paste="preventPaste"
												ref="pass"
												v-model="$v.reConfirmpassword.$model"
												@keypress.enter="resetPassword()"
												:class="{
													error: $v.reConfirmpassword.$error || match,
												}"
												placeholder="Confirm Password"
												class="login-input pr-5"
											></b-form-input>
										</div>
										<div
											class="invalid-msg-txt"
											v-if="$v.reConfirmpassword.$error || match"
										>
											{{
												$v.reConfirmpassword.$dirty &&
												!$v.reConfirmpassword.required
													? "Required"
													: !$v.reConfirmpassword.maxLength &&
													  $v.reConfirmpassword.required
													? "Should not exceed 55 characters"
													: match
													? "Password mismatch"
													: ""
											}}
										</div>
									</div>

									<b-button
										@click="resetPassword()"
										variant="primary"
										block
										class="login-submit-btn mb-4"
									>
										{{
											isResetPasswordMode ? "Reset Password" : "Confirm & Login"
										}}
									</b-button>

									<div class="text-center mt-2">
										<a
											href="javascript:void(0)"
											@click="mode = 'login'"
											class="back-to-login font-inter fw-500 color-blue text-decoration-none"
										>
											<i class="fas fa-arrow-left mr-1"></i> Back to login
										</a>
									</div>
								</b-overlay>
							</div>
						</template>

						<!-- Set Password Flow for Mobile -->
						<template v-else>
							<div class="mobile-set-password-container">
								<h3 class="mobile-set-password-title">
									{{
										isNewUserPasswordSetup
											? "Set Password"
											: isResetPasswordMode
											? "Reset Password"
											: "Set Password"
									}}
								</h3>
								<p class="mobile-set-password-subtitle">
									Securely activate your account password to be more secure
								</p>

								<!-- Sent Info Box with Envelope icon -->
								<div class="mob-sent-info-box mb-4">
									<div
										class="d-flex justify-content-between align-items-center w-100"
									>
										<div>
											<span class="mob-sent-label">OTP Sent on</span>
											<div class="d-flex align-items-center mt-2">
												<img
													v-if="useEmail"
													src="@/assets/img/homepage/tabler_mail.svg"
													width="20"
													height="20"
													class="mr-2 mail-icon"
													alt="Mail"
												/>
												<img
													v-else
													src="@/assets/img/homepage/solar_phone-outline.svg"
													width="20"
													height="20"
													class="mr-2 mail-icon"
													alt="Phone"
												/>
												<span class="mob-sent-email">
													{{
														useEmail
															? email
															: (loginPhoneCountryCode.startsWith("+")
																	? loginPhoneCountryCode
																	: "+" + loginPhoneCountryCode) +
															  "-" +
															  loginPhone.number
													}}
												</span>
											</div>
										</div>
										<a
											href="javascript:void(0)"
											@click="handleIdentifierEdit()"
											class="mob-sent-edit-btn"
											>Edit</a
										>
									</div>
								</div>

								<!-- Input Fields with 24px gap -->
								<div class="mobile-fields-container">
									<div class="mb-3">
										<div class="position-relative">
											<b-form-input
												ref="setPassField"
												v-model="$v.setPassword.$model"
												@input="validatePassword('setPassword')"
												:type="showSetPassword ? 'text' : 'password'"
												placeholder="Enter Password"
												class="single-field-update"
												:class="{
													error: $v.setPassword.$error || checkpassowrdInvalid,
												}"
											></b-form-input>
											<span
												class="password-toggle-icon"
												@click="showSetPassword = !showSetPassword"
											>
												<svg
													width="18"
													height="18"
													viewBox="0 0 24 24"
													fill="none"
													v-if="!showSetPassword"
												>
													<path
														d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
														stroke="#999"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
													<circle
														cx="12"
														cy="12"
														r="3"
														stroke="#999"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
												</svg>
												<svg
													width="18"
													height="18"
													viewBox="0 0 24 24"
													fill="none"
													v-else
												>
													<path
														d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"
														stroke="#999"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
												</svg>
											</span>
										</div>
										<div
											class="invalid-msg-txt mt-1"
											v-if="$v.setPassword.$error || checkpassowrdInvalid"
										>
											{{
												!$v.setPassword.required
													? "Required"
													: checkpassowrdInvalid
													? passwordValidation
													: !$v.setPassword.maxLength
													? "Should not exceed 55 characters"
													: ""
											}}
										</div>
									</div>

									<div>
										<b-form-input
											ref="confirmPassField"
											v-model="$v.confirmPassword.$model"
											type="password"
											placeholder="Confirm Password"
											class="single-field-update"
											:class="{ error: $v.confirmPassword.$error || match }"
										></b-form-input>
										<div
											class="invalid-msg-txt mt-1"
											v-if="$v.confirmPassword.$error"
										>
											{{
												!$v.confirmPassword.required
													? "Required"
													: !$v.confirmPassword.maxLength
													? "Should not exceed 55 characters"
													: ""
											}}
										</div>
										<div class="invalid-msg-txt mt-1" v-else-if="match">
											Passwords do not match
										</div>
									</div>
								</div>

								<!-- Enter OTP field for mobile Set Password -->
								<div v-if="showSetPasswordOtp" class="mt-3">
									<b-form-input
										ref="candotp"
										v-model.trim="$v.otpCode.$model"
										placeholder="Enter OTP"
										class="single-field-update"
										maxlength="6"
										:class="{ error: $v.otpCode.$error || invalidCredential }"
										@keypress="otpNumbersOnly"
									></b-form-input>
									<div
										class="invalid-msg-txt mt-1"
										v-if="$v.otpCode.$error && !$v.otpCode.required"
									>
										Required
									</div>
									<div
										class="invalid-msg-txt mt-1"
										v-else-if="$v.otpCode.$error"
									>
										Please enter a valid OTP
									</div>
									<div
										class="invalid-msg-txt mt-1"
										v-else-if="invalidCredential"
									>
										{{ invalidText }}
									</div>

									<div class="text-right mt-2" style="font-size: 13px">
										<span v-if="timerOut" style="color: #667085">
											{{
												otpExceeded
													? "OTP already sent."
													: "Didn't receive the OTP yet?"
											}}
											<span style="color: #28a745; font-weight: 500"
												>Resend in {{ formattedTime }}</span
											>
										</span>
										<a
											v-else
											href="javascript:void(0)"
											@click="
												isResetPasswordMode
													? forgotPassword('resend')
													: register('resend')
											"
											style="color: #4a7eff; font-weight: 500"
											>Resend OTP</a
										>
									</div>
								</div>

								<!-- Primary action button -->
								<b-button
									@click="handleSubmit()"
									variant="primary"
									block
									class="mob-continue-btn mt-4"
								>
									{{
										isResetPasswordMode ? "Reset Password" : "Confirm & Login"
									}}
								</b-button>
							</div>
						</template>
					</template>
					<template>
						<div v-if="setPasswordMode" class="set-password-container">
							<div>
								<!-- Title -->
								<h3 class="mobile-set-password-title">
									{{ isResetPasswordMode ? "Reset Password" : "Set Password" }}
								</h3>
								<p class="mobile-set-password-subtitle">
									Securely activate your account password to be more secure
								</p>

								<!-- Rounded Info Box (background gradient) -->
								<div class="otp-sent-info-box m-24px">
									<div
										class="d-flex justify-content-between align-items-center"
									>
										<div>
											<span class="info-box-label">OTP Sent on</span>
											<div class="d-flex align-items-center mt-2">
												<!-- Mail Icon -->
												<img
													v-if="useEmail"
													src="@/assets/img/homepage/tabler_mail.svg"
													width="24"
													height="24"
													class="mr-2"
													alt="Mail"
												/>
												<!-- Phone Icon -->
												<img
													v-else
													src="@/assets/img/homepage/solar_phone-outline.svg"
													width="24"
													height="24"
													class="mr-2"
													alt="Phone"
												/>

												<span class="info-box-value">
													{{
														useEmail
															? email
															: (loginPhoneCountryCode.startsWith("+")
																	? loginPhoneCountryCode
																	: "+" + loginPhoneCountryCode) +
															  "-" +
															  loginPhone.number
													}}
												</span>
											</div>
										</div>
										<a
											href="javascript:void(0)"
											@click="handleIdentifierEdit()"
											class="info-box-edit-link"
											>Edit</a
										>
									</div>
								</div>

								<div style="margin-top: 24px !important">
									<div>
										<div class="position-relative">
											<b-form-input
												v-model="$v.setPassword.$model"
												@input="validatePassword('setPassword')"
												:type="showSetPassword ? 'text' : 'password'"
												placeholder="Enter Password"
												style="margin-top: 0px !important"
												class="login-input single-field-update"
												:class="{
													error: $v.setPassword.$error || checkpassowrdInvalid,
												}"
											></b-form-input>
											<span
												class="password-toggle-icon"
												@click="showSetPassword = !showSetPassword"
												style="right: 24px"
											>
												<svg
													width="18"
													height="18"
													viewBox="0 0 24 24"
													fill="none"
													v-if="!showSetPassword"
												>
													<path
														d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
														stroke="#999"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
													<circle
														cx="12"
														cy="12"
														r="3"
														stroke="#999"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
												</svg>
												<svg
													width="18"
													height="18"
													viewBox="0 0 24 24"
													fill="none"
													v-else
												>
													<path
														d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"
														stroke="#999"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
												</svg>
											</span>
										</div>
										<div
											class="invalid-msg-txt mt-1"
											v-if="$v.setPassword.$error || checkpassowrdInvalid"
										>
											{{
												!$v.setPassword.required
													? "Required"
													: checkpassowrdInvalid
													? passwordValidation
													: !$v.setPassword.maxLength
													? "Should not exceed 55 characters"
													: ""
											}}
										</div>
									</div>

									<div class="mt-3">
										<b-form-input
											v-model="$v.confirmPassword.$model"
											type="password"
											placeholder="Confirm Password"
											class="login-input single-field-update"
											:class="{ error: $v.confirmPassword.$error || match }"
										></b-form-input>
										<div
											class="invalid-msg-txt mt-1"
											v-if="$v.confirmPassword.$error"
										>
											{{
												!$v.confirmPassword.required
													? "Required"
													: !$v.confirmPassword.maxLength
													? "Should not exceed 55 characters"
													: ""
											}}
										</div>
										<div class="invalid-msg-txt mt-1" v-else-if="match">
											Passwords do not match
										</div>
									</div>

									<!-- Enter OTP field for mobile Set Password -->
									<div v-if="showSetPasswordOtp" class="mt-3">
										<b-form-input
											ref="candotp"
											v-model.trim="$v.otpCode.$model"
											placeholder="Enter OTP"
											class="single-field-update"
											maxlength="6"
											:class="{ error: $v.otpCode.$error || invalidCredential }"
											@keypress="otpNumbersOnly"
										></b-form-input>
										<div
											class="invalid-msg-txt mt-1"
											v-if="$v.otpCode.$error && !$v.otpCode.required"
										>
											Required
										</div>
										<div
											class="invalid-msg-txt mt-1"
											v-else-if="$v.otpCode.$error"
										>
											Please enter a valid OTP
										</div>
										<div
											class="invalid-msg-txt mt-1"
											v-else-if="invalidCredential"
										>
											{{ invalidText }}
										</div>

										<div class="text-right mt-2" style="font-size: 13px">
											<span v-if="timerOut" style="color: #667085">
												{{
													otpExceeded
														? "OTP already sent."
														: "Didn't receive the OTP yet?"
												}}
												<span style="color: #28a745; font-weight: 500"
													>Resend in {{ formattedTime }}</span
												>
											</span>
											<a
												v-else
												href="javascript:void(0)"
												@click="
													isResetPasswordMode || isNewUserPasswordSetup
														? forgotPassword('resend')
														: register('resend')
												"
												style="color: #4a7eff; font-weight: 500"
												>Resend OTP</a
											>
										</div>
									</div>
								</div>
							</div>

							<b-button
								@click="handleSubmit()"
								variant="primary"
								block
								class="login-submit-btn mt-2"
								style="background-color: #4a7eff !important"
							>
								{{
									showSetPasswordOtp
										? isNewUserPasswordSetup
											? "LogIn"
											: isResetPasswordMode
											? "Reset Password"
											: "Confirm & Login"
										: "Send OTP"
								}}
							</b-button>
						</div>
					</template>
					<Popupcomponent
						v-if="showPopup || registered"
						:showPopup="showPopup"
						:registered="registered"
						:candExists="candExists"
						:invalidText="invalidText"
						:successText="successText"
					/>
				</div>
			</b-overlay>
		</div>

		<!-- Desktop View -->
		<div v-else class="login-modal-container">
			<!-- Close Button -->
			<button
				v-if="!isRoute && mode !== 'signup'"
				class="close-modal-btn"
				@click="$emit('close')"
			>
				<span>✕</span>
			</button>

			<div class="row no-gutters">
				<!-- Left Side: Content Box -->
				<div class="col-lg-5 d-none d-lg-block left-panel">
					<div class="left-content h-100 d-flex flex-column">
						<!-- Business Side: Stable image -->
						<div
							v-if="userType === 'admin'"
							class="illustration-placeholder mt-auto position-relative flex-grow-1 overflow-hidden"
						>
							<img
								src="@/assets/img/homepage/wave-boy.png"
								class="character-img h-100 position-absolute"
								alt="Login illustration"
							/>
						</div>

						<!-- Candidate Side: Carousel slider -->
						<template v-else>
							<div
								class="illustration-placeholder mt-auto position-relative flex-grow-1 overflow-hidden"
							>
								<transition-group name="fade" tag="div" class="h-100">
									<img
										v-for="(img, index) in sliderImages"
										:key="'img-' + index"
										v-show="sliderIndex === index"
										:src="img"
										class="character-img h-100 position-absolute"
										alt="Login illustration"
									/>
								</transition-group>
							</div>
							<!-- Slider dots -->
							<div class="slider-dots-container mb-4">
								<div class="slider-dots d-flex justify-content-center">
									<span
										v-for="(dot, index) in sliderImages"
										:key="'dot-' + index"
										class="dot"
										:class="{ active: sliderIndex === index }"
										@click="setSlide(index)"
									></span>
								</div>
							</div>
						</template>
					</div>
				</div>

				<!-- Right Side: Form Content -->
				<div class="col-lg-7 col-12 right-panel">
					<!-- Login View -->
					<div v-if="mode === 'login'" class="login-form-wrapper">
						<b-overlay
							:show="show"
							:opacity="overlayOpacity"
							rounded="sm"
							class="h-100"
						>
							<template #overlay>
								<div class="overlaybox">
									<b-spinner class="login-loader"></b-spinner>
									<span class="overlaytxt">{{ loaderLabel }}</span>
								</div>
							</template>

							<div v-if="!setPasswordMode">
								<!-- Toggle Switch -->
								<div class="toggle-container">
									<div class="toggle-track d-flex w-100">
										<button
											class="flex-fill toggle-btn"
											:class="{ active: userType === 'candidate' }"
											@click="userType = 'candidate'"
										>
											Candidate
										</button>
										<!-- <button
											v-if="!hideBusiness"
											class="flex-fill toggle-btn"
											:class="{ active: userType === 'admin' }"
											@click="userType = 'admin'"
										>
											Business
										</button> -->
									</div>
								</div>
							</div>
							<div v-else-if="setPasswordMode" class="pr-3 mb-3">
								<h3 class="mb-2 mobile-set-password-title">
									{{
										isNewUserPasswordSetup
											? "Set Password"
											: isResetPasswordMode
											? "Reset Password"
											: "Set Password"
									}}
								</h3>
								<p class="mobile-set-password-subtitle">
									Securely activate your account password to be more secure
								</p>
							</div>

							<div
								class="login-overflow"
								:class="{ 'has-errors': hasSetPasswordErrors }"
							>
								<div v-if="!setPasswordMode">
									<!-- Google SSO (Candidate only) -->
									<template v-if="userType === 'candidate'">
										<b-button
											variant="outline-light"
											block
											class="google-login-btn"
											@click="googleLogin('newlogin')"
											:disabled="show"
										>
											<img
												src="@/assets/img/homepage/google-up.svg"
												height="20"
												class="mr-2"
												alt="Google"
											/>
											Login with Google
										</b-button>
										<div
											class="invalid-msg-txt mt-2"
											v-if="invalidCredential && !otpSent && !setPasswordMode"
										>
											{{ invalidText }}
										</div>
										<div class="separator mt-4 mb-4">
											<span>Sign up / Login with</span>
										</div>
									</template>
								</div>

								<!-- Inputs -->
								<template v-if="userType === 'candidate'">
									<div class="mb-4">
										<!-- Phone Input -->
										<!-- Set Password Section -->
										<div v-if="setPasswordMode" class="set-password-container">
											<div>
												<!-- Rounded Info Box (background gradient) -->
												<div class="otp-sent-info-box mb-4">
													<div
														class="d-flex justify-content-between align-items-center"
													>
														<div>
															<span class="info-box-label">OTP Sent on</span>
															<div class="d-flex align-items-center mt-2">
																<!-- Mail Icon -->
																<img
																	v-if="useEmail"
																	src="@/assets/img/homepage/tabler_mail.svg"
																	width="24"
																	height="24"
																	class="mr-2"
																	alt="Mail"
																/>
																<!-- Phone Icon -->
																<img
																	v-else
																	src="@/assets/img/homepage/solar_phone-outline.svg"
																	width="24"
																	height="24"
																	class="mr-2"
																	alt="Phone"
																/>

																<span class="info-box-value">
																	{{
																		useEmail
																			? email
																			: (loginPhoneCountryCode.startsWith("+")
																					? loginPhoneCountryCode
																					: "+" + loginPhoneCountryCode) +
																			  "-" +
																			  loginPhone.number
																	}}
																</span>
															</div>
														</div>
														<a
															href="javascript:void(0)"
															@click="handleIdentifierEdit()"
															class="info-box-edit-link"
															>Edit</a
														>
													</div>
												</div>

												<div style="margin-top: 24px !important">
													<div class="position-relative mb-4">
														<b-form-input
															ref="setPassField"
															v-model="$v.setPassword.$model"
															@input="validatePassword('setPassword')"
															:type="showSetPassword ? 'text' : 'password'"
															placeholder="Enter Password"
															class="login-input single-field-update"
															:class="{
																error:
																	$v.setPassword.$error || checkpassowrdInvalid,
															}"
														></b-form-input>
														<span
															class="password-toggle-icon"
															@click="showSetPassword = !showSetPassword"
															style="right: 24px"
														>
															<svg
																width="18"
																height="18"
																viewBox="0 0 24 24"
																fill="none"
																v-if="!showSetPassword"
															>
																<path
																	d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
																	stroke="#999"
																	stroke-width="2"
																	stroke-linecap="round"
																	stroke-linejoin="round"
																/>
																<circle
																	cx="12"
																	cy="12"
																	r="3"
																	stroke="#999"
																	stroke-width="2"
																	stroke-linecap="round"
																	stroke-linejoin="round"
																/>
															</svg>
															<svg
																width="18"
																height="18"
																viewBox="0 0 24 24"
																fill="none"
																v-else
															>
																<path
																	d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"
																	stroke="#999"
																	stroke-width="2"
																	stroke-linecap="round"
																	stroke-linejoin="round"
																/>
															</svg>
														</span>
														<div
															class="invalid-msg-txt mt-1"
															v-if="
																$v.setPassword.$error || checkpassowrdInvalid
															"
														>
															{{
																!$v.setPassword.required
																	? "Required"
																	: checkpassowrdInvalid
																	? passwordValidation
																	: !$v.setPassword.maxLength
																	? "Should not exceed 55 characters"
																	: ""
															}}
														</div>
													</div>

													<div class="mb-4">
														<b-form-input
															ref="confirmPassField"
															v-model="$v.confirmPassword.$model"
															type="password"
															placeholder="Confirm Password"
															class="login-input single-field-update"
															:class="{
																error: $v.confirmPassword.$error || match,
															}"
														></b-form-input>
														<div
															class="invalid-msg-txt mt-1"
															v-if="$v.confirmPassword.$error"
														>
															{{
																!$v.confirmPassword.required
																	? "Required"
																	: !$v.confirmPassword.maxLength
																	? "Should not exceed 55 characters"
																	: ""
															}}
														</div>
														<div class="invalid-msg-txt mt-1" v-else-if="match">
															Passwords do not match
														</div>
													</div>

													<!-- Enter OTP field for web Set Password -->
													<div v-if="showSetPasswordOtp" class="mb-4">
														<b-form-input
															ref="candotp"
															v-model.trim="$v.otpCode.$model"
															type="text"
															placeholder="Enter OTP"
															class="login-input single-field-update"
															maxlength="6"
															@keypress.enter="handleSubmit()"
															:class="{
																error: $v.otpCode.$error || invalidCredential,
															}"
															@keypress="otpNumbersOnly"
														></b-form-input>
														<div
															class="invalid-msg-txt mt-1"
															v-if="$v.otpCode.$error && !$v.otpCode.required"
														>
															Required
														</div>
														<div
															class="invalid-msg-txt mt-1"
															v-else-if="$v.otpCode.$error"
														>
															Please enter a valid OTP
														</div>
														<div
															class="invalid-msg-txt mt-1"
															v-else-if="invalidCredential"
														>
															{{ invalidText }}
														</div>

														<div
															class="text-right mt-2"
															style="font-size: 13px"
														>
															<span v-if="timerOut" style="color: #667085">
																{{
																	otpExceeded
																		? "OTP already sent."
																		: "Didn't receive the OTP yet?"
																}}
																<span style="color: #28a745; font-weight: 500"
																	>Resend in {{ formattedTime }}</span
																>
															</span>
															<a
																v-else
																href="javascript:void(0)"
																@click="
																	isResetPasswordMode || isNewUserPasswordSetup
																		? forgotPassword('resend')
																		: register('resend')
																"
																style="color: #4a7eff; font-weight: 500"
																>Resend OTP</a
															>
														</div>
													</div>
												</div>
											</div>

											<b-button
												@click="handleSubmit()"
												variant="primary"
												block
												class="login-submit-btn"
												style="background-color: #4a7eff !important"
											>
												{{
													showSetPasswordOtp
														? isNewUserPasswordSetup
															? "LogIn"
															: isResetPasswordMode
															? "Reset Password"
															: "Confirm & Login"
														: "Send OTP"
												}}
											</b-button>
										</div>

										<!-- Normal Candidate Phone Login Form -->
										<div v-else-if="!useEmail">
											<div class="input-field-container">
												<b-form-input
													ref="phoneInput"
													id="candidatePhone"
													type="tel"
													v-model.trim="loginPhone.number"
													autocomplete="off"
													maxlength="11"
													@keypress.enter="handleSubmit()"
													@keypress="otpNumbersOnly"
													:class="{
														inputError: loginPhoneInvalid && !otpSent,
													}"
													placeholder="Phone Number"
													class="phone-input w-100 no-number"
													:disabled="otpSent"
												></b-form-input>
												<span
													v-if="otpSent"
													class="phone-edit-action"
													@click="handleEdit()"
												>
													Edit
												</span>
											</div>
											<div
												class="invalid-msg-txt mt-1"
												v-if="loginPhoneInvalid && !otpSent"
											>
												Invalid Phone Number
											</div>
											<div
												class="invalid-msg-txt mt-1"
												v-if="sendOtpError && !useEmail && !otpExceeded"
											>
												{{ sendOtpError }}
											</div>
											<div v-if="!otpSent" class="text-right mt-2 mb-3">
												<a
													href="javascript:void(0)"
													@click="toggleLoginMode(true)"
													class="toggle-input-link"
												>
													Use E-mail ID instead
												</a>
											</div>

											<!-- OTP Field (placed after phone number field) -->
											<div v-if="useOtp && otpSent" class="mt-3">
												<b-form-input
													ref="candotp"
													v-model.trim="otpCode"
													type="text"
													placeholder="Enter OTP"
													class="login-input single-field-update"
													maxlength="6"
													@keypress.enter="handleSubmit()"
													:class="{
														error: $v.otpCode.$error || invalidCredential,
													}"
													@keypress="otpNumbersOnly"
												></b-form-input>
												<div
													class="invalid-msg-txt mt-1"
													v-if="$v.otpCode.$error && !$v.otpCode.required"
												>
													Required
												</div>
												<div
													class="invalid-msg-txt mt-1"
													v-else-if="$v.otpCode.$error"
												>
													Please enter a valid OTP
												</div>
												<div
													class="invalid-msg-txt mt-1"
													v-else-if="invalidCredential && !setPasswordMode"
												>
													{{ invalidText }}
												</div>
											</div>

											<!-- Password Field (if they choose to use password) -->
											<div v-if="!useOtp" class="mt-3 position-relative">
												<b-form-input
													v-model="$v.password.$model"
													:type="showPassword ? 'text' : 'password'"
													placeholder="Enter Password"
													class="login-input single-field-update"
													ref="candpass"
													@keypress.enter="handleSubmit()"
													autocomplete="off"
													:class="{ error: $v.password.$error }"
												></b-form-input>
												<span
													class="password-toggle-icon"
													@click="showPassword = !showPassword"
													style="right: 24px"
												>
													<svg
														width="18"
														height="18"
														viewBox="0 0 24 24"
														fill="none"
														v-if="!showPassword"
													>
														<path
															d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
															stroke="#999"
															stroke-width="2"
															stroke-linecap="round"
															stroke-linejoin="round"
														/>
														<circle
															cx="12"
															cy="12"
															r="3"
															stroke="#999"
															stroke-width="2"
															stroke-linecap="round"
															stroke-linejoin="round"
														/>
													</svg>
													<svg
														width="18"
														height="18"
														viewBox="0 0 24 24"
														fill="none"
														v-else
													>
														<path
															d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"
															stroke="#999"
															stroke-width="2"
															stroke-linecap="round"
															stroke-linejoin="round"
														/>
													</svg>
												</span>
											</div>
											<div
												class="invalid-msg-txt mt-1"
												v-if="!useOtp && $v.password.$error"
											>
												{{
													$v.password.$dirty && !$v.password.required
														? "Required"
														: !$v.password.maxLength && $v.password.required
														? "Should not execed 55 characters"
														: ""
												}}
											</div>
											<div
												class="invalid-msg-txt mt-1"
												v-else-if="!useOtp && invalidCredential"
											>
												{{ invalidText }}
											</div>

											<!-- Links under the input -->
											<!-- Case 1: When showing OTP (phone) -->
											<div
												v-if="useOtp && otpSent"
												class="d-flex justify-content-between align-items-center otp-links-container mt-3"
											>
												<!-- Left: Set password / Use password links -->
												<a
													v-if="userExists === false"
													href="javascript:void(0)"
													@click="openPasswordSetup()"
													class="toggle-input-link"
												>
													Set password to sign up
												</a>
												<a
													v-else-if="userExists === true"
													href="javascript:void(0)"
													@click="usePasswordOrSetup()"
													class="toggle-input-link"
												>
													Use password
												</a>
												<span v-else></span>

												<!-- Right: Resend Timer / Resend OTP -->
												<span v-if="timerOut" class="otp-timer-text">
													{{
														otpExceeded
															? "OTP already sent. Resend in"
															: "Resend in"
													}}
													<span class="time-bold">{{ formattedTime }}</span>
												</span>
												<a
													v-else
													href="javascript:void(0)"
													@click="register('resend')"
													class="toggle-input-link"
												>
													Resend OTP
												</a>
											</div>

											<!-- Case 2: When showing Password (phone) -->
											<div
												v-if="!useOtp"
												class="d-flex justify-content-between align-items-center otp-links-container mt-3"
											>
												<!-- Left: Login with OTP (underlined) -->
												<a
													href="javascript:void(0)"
													@click="useOtpLogin()"
													class="toggle-input-link text-decoration-underline"
												>
													Login with OTP
												</a>

												<!-- Right: Forgot Password? -->
												<a
													href="javascript:void(0)"
													@click="
														userType === 'candidate'
															? openPasswordSetup(true)
															: (mode = 'forgot')
													"
													class="forgot-password-link"
												>
													Forgot Password?
												</a>
											</div>
										</div>

										<!-- Email Input -->
										<div v-else>
											<div class="position-relative">
												<b-form-input
													v-model="$v.email.$model"
													@keydown.space.prevent
													ref="candmail"
													@keypress.enter="handleSubmit()"
													@input="sendOtpError = ''"
													type="email"
													placeholder="Enter Email ID"
													class="login-input single-field-update"
													:class="{
														error:
															$v.email.$error || (sendOtpError && !otpExceeded),
													}"
													:disabled="otpSent"
												></b-form-input>
												<a
													v-if="otpSent"
													href="javascript:void(0)"
													@click="handleIdentifierEdit()"
													class="desktop-email-edit-btn"
													>Edit</a
												>
											</div>
											<div
												class="invalid-msg-txt mt-1"
												v-if="$v.email.$error && !otpSent"
											>
												{{
													$v.email.$dirty && !$v.email.required
														? "Required"
														: $v.email.required &&
														  (!$v.email.email || !$v.email.alphaEmail)
														? "Invalid Email"
														: !$v.email.maxLength
														? "Should not execed 64 characters"
														: ""
												}}
											</div>
											<div
												class="invalid-msg-txt mt-1"
												v-if="sendOtpError && useEmail && !otpExceeded"
											>
												{{ sendOtpError }}
											</div>

											<!-- Password Field for Email (placed after email input field) -->
											<div v-if="!useOtp" class="mt-3 position-relative">
												<b-form-input
													v-model="$v.password.$model"
													:type="showPassword ? 'text' : 'password'"
													placeholder="Password"
													class="login-input password-input"
													ref="candpass"
													@keypress.enter="handleSubmit()"
													autocomplete="off"
													:class="{ error: $v.password.$error }"
												></b-form-input>
												<span
													class="password-toggle-icon"
													@click="showPassword = !showPassword"
													style="right: 24px"
												>
													<svg
														width="18"
														height="18"
														viewBox="0 0 24 24"
														fill="none"
														v-if="!showPassword"
													>
														<circle
															cx="12"
															cy="12"
															r="3"
															stroke="#999"
															stroke-width="2"
															stroke-linecap="round"
															stroke-linejoin="round"
														/>
														<path
															d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
															stroke="#999"
															stroke-width="2"
															stroke-linecap="round"
															stroke-linejoin="round"
														/>
													</svg>
													<svg
														width="18"
														height="18"
														viewBox="0 0 24 24"
														fill="none"
														v-else
													>
														<path
															d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"
															stroke="#999"
															stroke-width="2"
															stroke-linecap="round"
															stroke-linejoin="round"
														/>
													</svg>
												</span>
											</div>
											<div
												class="invalid-msg-txt mt-1"
												v-if="!useOtp && $v.password.$error"
											>
												{{
													$v.password.$dirty && !$v.password.required
														? "Required"
														: !$v.password.maxLength && $v.password.required
														? "Should not execed 55 characters"
														: ""
												}}
											</div>
											<div
												class="invalid-msg-txt mt-1"
												v-else-if="!useOtp && invalidCredential"
											>
												{{ invalidText }}
											</div>

											<!-- OTP Field (placed after email input field) -->
											<div v-if="useOtp && otpSent" class="mt-3">
												<b-form-input
													ref="candotp"
													v-model.trim="otpCode"
													type="text"
													placeholder="Enter OTP"
													class="login-input single-field-update"
													maxlength="6"
													@keypress.enter="handleSubmit()"
													@keypress="otpNumbersOnly"
													:class="{
														error: $v.otpCode.$error || invalidCredential,
													}"
												></b-form-input>
												<div
													class="invalid-msg-txt mt-1"
													v-if="$v.otpCode.$error && !$v.otpCode.required"
												>
													Required
												</div>
												<div
													class="invalid-msg-txt mt-1"
													v-else-if="$v.otpCode.$error"
												>
													Please enter a valid OTP
												</div>
												<div
													class="invalid-msg-txt mt-1"
													v-else-if="invalidCredential"
												>
													{{ invalidText }}
												</div>
											</div>

											<!-- Links under the input for Email -->
											<!-- Case 1: When showing OTP (email) -->
											<div
												v-if="useOtp && otpSent"
												class="d-flex justify-content-between align-items-center otp-links-container mt-3"
											>
												<!-- Left: Set password / Use password links -->
												<a
													v-if="userExists === false"
													href="javascript:void(0)"
													@click="openPasswordSetup()"
													class="toggle-input-link"
												>
													Set password to sign up
												</a>
												<a
													v-else-if="userExists === true"
													href="javascript:void(0)"
													@click="usePasswordOrSetup()"
													class="toggle-input-link"
												>
													Use password
												</a>
												<span v-else></span>

												<!-- Right: Resend Timer / Resend OTP -->
												<span v-if="timerOut" class="otp-timer-text">
													{{
														otpExceeded
															? "OTP already sent. Resend in"
															: "Resend in"
													}}
													<span class="time-bold">{{ formattedTime }}</span>
												</span>
												<a
													v-else
													href="javascript:void(0)"
													@click="register('resend')"
													class="toggle-input-link"
												>
													Resend OTP
												</a>
											</div>

											<!-- Case 2: When showing Password (email) -->
											<div
												v-if="!useOtp"
												class="d-flex justify-content-between align-items-center otp-links-container mt-3"
											>
												<!-- Left: Login with OTP (underlined) -->
												<a
													href="javascript:void(0)"
													@click="useOtpLogin()"
													class="toggle-input-link text-decoration-underline"
												>
													Login with OTP
												</a>

												<!-- Right: Forgot Password? -->
												<a
													href="javascript:void(0)"
													@click="
														userType === 'candidate'
															? openPasswordSetup(true)
															: (mode = 'forgot')
													"
													class="forgot-password-link"
												>
													Forgot Password?
												</a>
											</div>

											<div
												v-if="userType === 'candidate' && !otpSent"
												class="text-right mt-2"
											>
												<a
													href="javascript:void(0)"
													@click="toggleLoginMode(false)"
													class="toggle-input-link"
												>
													Use Phone Number instead
												</a>
											</div>
										</div>
									</div>
									<div v-if="!setPasswordMode">
										<b-button
											variant="primary"
											@click="handleSubmit()"
											block
											class="login-submit-btn"
											:disabled="
												userType === 'candidate' &&
												useOtp &&
												!otpSent &&
												(useEmail
													? !email || email.trim() === ''
													: !loginPhone.number ||
													  loginPhone.number.trim() === '')
											"
										>
											{{
												userType === "candidate" && useOtp
													? !otpSent
														? "Continue with OTP"
														: userExists === false
														? "Sign up"
														: "Login"
													: "Login"
											}}
										</b-button>

										<!-- Google SSO (Candidate only) -->
										<!--
                  <template v-if="userType === 'candidate'">
                    <div class="text-center">
                      <p class="signup-text font-inter">
                        Don't have an account yet?
                        <a
                          href="javascript:void(0)"
                          @click="mode = 'register'"
                          class="register-link"
                          >Register here</a
                        >
                      </p>
                    </div>
                  </template>
            -->

										<template v-if="userType === 'candidate'">
											<div class="text-center mt-4">
												<p class="terms-privacy-text">
													By Continuing, you accept to the
													<a
														href="javascript:void(0)"
														@click="terms()"
														class="terms-link"
														>Terms and Conditions</a
													>
													and
													<a
														href="javascript:void(0)"
														@click="goToPrivacyPolicy()"
														class="terms-link"
														>Privacy Policy</a
													>
													of Hire3x.com
												</p>
											</div>
										</template>
										<div v-else class="text-center">
											<p class="signup-text font-inter">
												Don't have an account yet?
												<a @click="goToDemo()" class="register-link pointer"
													>Request Demo</a
												>
											</p>
										</div>
									</div>
								</template>

								<!-- Business login web -->
								<template v-else>
									<div class="mb-3 mt-4">
										<b-form-input
											v-model="$v.email.$model"
											@keydown.space.prevent
											ref="candmail"
											@keypress.enter="handleSubmit()"
											type="email"
											placeholder="Email"
											class="login-input single-field-update"
											:class="{ error: $v.email.$error }"
										></b-form-input>
										<div class="invalid-msg-txt mt-1" v-if="$v.email.$error">
											{{
												$v.email.$dirty && !$v.email.required
													? "Required"
													: $v.email.required &&
													  (!$v.email.email || !$v.email.alphaEmail)
													? "Invalid Email"
													: !$v.email.maxLength
													? "Should not execed 64 characters"
													: ""
											}}
										</div>
									</div>
									<div class="mb-2">
										<div class="position-relative">
											<b-form-input
												v-model="$v.password.$model"
												:type="showPassword ? 'text' : 'password'"
												placeholder="Password"
												class="login-input single-field-update"
												ref="candpass"
												@keypress.enter="handleSubmit()"
												autocomplete="off"
												:class="{ error: $v.password.$error }"
											></b-form-input>
											<span
												class="password-toggle-icon"
												@click="showPassword = !showPassword"
											>
												<svg
													width="18"
													height="18"
													viewBox="0 0 24 24"
													fill="none"
													v-if="!showPassword"
												>
													<path
														d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
														stroke="#999"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
													<circle
														cx="12"
														cy="12"
														r="3"
														stroke="#999"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
												</svg>
												<svg
													width="18"
													height="18"
													viewBox="0 0 24 24"
													fill="none"
													v-else
												>
													<path
														d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"
														stroke="#999"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
												</svg>
											</span>
										</div>
										<div class="invalid-msg-txt mt-1" v-if="$v.password.$error">
											{{
												$v.password.$dirty && !$v.password.required
													? "Required"
													: !$v.password.maxLength && $v.password.required
													? "Should not execed 55 characters"
													: ""
											}}
										</div>

										<div
											class="invalid-msg-txt mt-1"
											v-else-if="invalidCredential"
										>
											{{ invalidText }}
										</div>
									</div>
									<div class="text-left mb-4">
										<a
											href="javascript:void(0)"
											class="forgot-link"
											@click="mode = 'forgot'"
											>Forgot your password?</a
										>
									</div>

									<b-button
										variant="primary"
										@click="handleSubmit()"
										block
										class="login-submit-btn mb-3"
									>
										Login
									</b-button>
									<div class="text-center">
										<p class="signup-text font-inter">
											Don't have an account yet?
											<a @click="goToDemo()" class="register-link pointer"
												>Request Demo</a
											>
										</p>
									</div>
								</template>
							</div>
						</b-overlay>
					</div>

					<!-- Forgot Password View Web -->
					<div
						v-if="mode === 'forgot'"
						class="login-form-wrapper-2 forgot-pass d-flex flex-column"
					>
						<b-overlay
							:show="show"
							:opacity="overlayOpacity"
							rounded="sm"
							class="h-100"
						>
							<template #overlay>
								<div class="overlaybox">
									<b-spinner class="login-loader"></b-spinner>
									<span class="overlaytxt">Loading</span>
								</div>
							</template>
							<h3 class="font-inter fw-600 color-darkblack mb-2 fs-22 mt-4">
								Forgot Passwords?
							</h3>
							<p class="font-inter color-gray mb-4 fs-14">
								Enter the email address associated with this account.
							</p>

							<div class="mb-4">
								<b-form-input
									type="email"
									placeholder="Email"
									class="login-input single-field-update"
									v-model="$v.email.$model"
									@keydown.space.prevent
									ref="candmail"
									@keypress.enter="forgotPassword()"
									:class="{ error: $v.email.$error || invalidCredential }"
								></b-form-input>
								<div
									class="invalid-msg-txt"
									v-if="$v.email.$error || invalidCredential"
								>
									{{
										$v.email.$dirty && !$v.email.required
											? "Required"
											: $v.email.required &&
											  (!$v.email.email || !$v.email.alphaEmail)
											? "Invalid Email"
											: !$v.email.maxLength
											? "Should not execed 64 characters"
											: invalidCredential
											? invalidText
											: ""
									}}
								</div>
							</div>

							<b-button
								@click="forgotPassword()"
								variant="primary"
								block
								class="login-submit-btn mb-4"
							>
								Send OTP
							</b-button>

							<div class="text-center mt-2">
								<a
									href="javascript:void(0)"
									@click="mode = 'login'"
									class="back-to-login font-inter fw-500 color-blue text-decoration-none"
								>
									<i class="fas fa-arrow-left mr-1"></i> Back to login
								</a>
							</div>
						</b-overlay>
					</div>

					<!-- Verify OTP View Web -->
					<div
						v-if="
							(mode === 'otp' || mode === 'forgotOtp') &&
							userType !== 'candidate'
						"
						class="login-form-wrapper-2 forgotOtp d-flex flex-column"
					>
						<b-overlay
							:show="show"
							:opacity="overlayOpacity"
							rounded="sm"
							class="h-100"
						>
							<template #overlay>
								<div class="overlaybox">
									<b-spinner class="login-loader"></b-spinner>
									<span class="overlaytxt">Loading</span>
								</div>
							</template>
							<hr
								id="hideinmobile"
								style="border-top: 1px solid #dee2e6"
								class="mb-4 mt-4"
							/>
							<h3 class="font-inter fw-600 color-darkblack mb-2 fs-22">
								Verify OTP
							</h3>
							<p class="font-inter color-gray mb-4 fs-14">
								Please enter the 6 digits OTP sent to :-
								<a
									href="javascript:void(0)"
									class="color-blue text-decoration-none"
								>
									{{ formatEmail }}</a
								>
							</p>

							<div
								class="d-flex otp-input-row justify-content-between"
								:class="
									invalidCredential ||
									($v.otpDigits.$dirty && $v.otpDigits.$invalid && verifyotp)
										? 'mb-1'
										: 'mb-4'
								"
							>
								<input
									ref="firstInput"
									type="number"
									:class="{
										error:
											invalidCredential ||
											($v.otpDigits.num1.$error && verifyotp),
									}"
									maxlength="1"
									placeholder="0"
									@keydown="moveToNextField($event, 'secondInput', null)"
									@keypress="restrictToOneDigit"
									class="otp-box no-number"
									v-model="$v.otpDigits.num1.$model"
								/>
								<input
									ref="secondInput"
									type="number"
									:class="{
										error:
											invalidCredential ||
											($v.otpDigits.num2.$error && verifyotp),
									}"
									@keydown="moveToNextField($event, 'thirdInput', 'firstInput')"
									@keypress="restrictToOneDigit"
									@paste="preventPaste"
									placeholder="0"
									maxlength="1"
									class="otp-box no-number"
									v-model="$v.otpDigits.num2.$model"
								/>
								<input
									ref="thirdInput"
									type="number"
									:class="{
										error:
											invalidCredential ||
											($v.otpDigits.num3.$error && verifyotp),
									}"
									@keydown="
										moveToNextField($event, 'fourthInput', 'secondInput')
									"
									@keypress="restrictToOneDigit"
									@paste="preventPaste"
									placeholder="0"
									maxlength="1"
									class="otp-box no-number"
									v-model="$v.otpDigits.num3.$model"
								/>
								<input
									ref="fourthInput"
									type="number"
									:class="{
										error:
											invalidCredential ||
											($v.otpDigits.num4.$error && verifyotp),
									}"
									@keydown="moveToNextField($event, 'fifthInput', 'thirdInput')"
									@keypress="restrictToOneDigit"
									@paste="preventPaste"
									placeholder="0"
									maxlength="1"
									class="otp-box no-number"
									v-model="$v.otpDigits.num4.$model"
								/>
								<input
									ref="fifthInput"
									type="number"
									:class="{
										error:
											invalidCredential ||
											($v.otpDigits.num5.$error && verifyotp),
									}"
									@keydown="
										moveToNextField($event, 'sixthInput', 'fourthInput')
									"
									@keypress="restrictToOneDigit"
									@paste="preventPaste"
									placeholder="0"
									maxlength="1"
									class="otp-box no-number"
									v-model="$v.otpDigits.num5.$model"
								/>
								<input
									ref="sixthInput"
									type="number"
									:class="{
										error:
											invalidCredential ||
											($v.otpDigits.num6.$error && verifyotp),
									}"
									@keypress="restrictToOneDigit"
									@keypress.enter="
										mode === 'otp' ? handleSubmit() : resetPasswordOTP()
									"
									@keydown="moveToNextField($event, null, 'fifthInput')"
									@paste="preventPaste"
									placeholder="0"
									maxlength="1"
									class="otp-box no-number"
									v-model="$v.otpDigits.num6.$model"
								/>
							</div>
							<div class="invalid-msg-txt" v-if="invalidCredential">
								{{ invalidText }}
							</div>
							<div
								class="invalid-msg-txt"
								v-else-if="
									$v.otpDigits.$dirty && $v.otpDigits.$invalid && verifyotp
								"
							>
								Enter a valid otp
							</div>
							<template v-if="!otpExceeded">
								<div class="text-left mb-4">
									<span class="font-inter fs-13 color-gray"
										>Didn't receive code?
									</span>
									<span v-if="timerOut" class="font-inter fs-13 color-gray">
										Resend in
										<span
											v-if="timerOut"
											class="fw-500 fs-13 text-decoration-none"
											style="color: red"
											>{{ formattedTime }}</span
										>
									</span>
									<a
										v-else
										href="javascript:void(0)"
										@click="
											mode === 'otp'
												? register('resend')
												: forgotPassword('resend')
										"
										class="color-blue fw-500 text-decoration-none"
										>Resend OTP</a
									>
								</div>
							</template>
							<div v-else class="text-left mb-4">
								<span class="fam-inter fs-13 color-gray"
									>OTP has already been sent.<br v-if="isMobile" />
									You can request a new OTP in
								</span>
								<span
									class="fw-500 fs-13 text-decoration-none"
									style="color: red"
									>{{ formattedTime }}</span
								>
							</div>
							<b-button
								@click="mode === 'otp' ? handleSubmit() : resetPasswordOTP()"
								variant="primary"
								block
								class="login-submit-btn mb-4"
							>
								Verify
							</b-button>

							<div class="text-center mt-2">
								<a
									href="javascript:void(0)"
									@click="mode = 'login'"
									class="back-to-login font-inter fw-500 color-blue text-decoration-none"
								>
									<i class="fas fa-arrow-left mr-1"></i> Back to login
								</a>
							</div>
						</b-overlay>
					</div>

					<!-- Reset Password View Web -->
					<div
						v-if="mode === 'reset'"
						class="login-form-wrapper-2 reset d-flex flex-column"
					>
						<b-overlay
							:show="show"
							:opacity="overlayOpacity"
							rounded="sm"
							class="h-100"
						>
							<template #overlay>
								<div class="overlaybox">
									<b-spinner class="login-loader"></b-spinner>
									<span class="overlaytxt">Loading</span>
								</div>
							</template>
							<hr
								id="hideinmobile"
								style="border-top: 1px solid #dee2e6"
								class="mb-4 mt-4"
							/>
							<h3 class="font-inter fw-600 color-darkblack mb-2 fs-22">
								Reset Password
							</h3>
							<p class="font-inter color-gray mb-4 fs-14">
								Please enter the new password to reset your password.
							</p>
							<div class="mb-3">
								<div class="position-relative">
									<b-form-input
										:type="showPass ? 'text' : 'password'"
										@input="validatePassword('rePassword')"
										v-model="$v.rePassword.$model"
										ref="passnew"
										placeholder="New Password"
										class="login-input single-field-update pr-5"
										@keypress.enter="resetPassword()"
										:class="{
											error: $v.rePassword.$error || checkpassowrdInvalid,
										}"
									></b-form-input>
									<span
										class="password-toggle-icon"
										@click="showPass = !showPass"
									>
										<svg
											width="18"
											height="18"
											viewBox="0 0 24 24"
											fill="none"
											v-if="!showPass"
										>
											<path
												d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
												stroke="#999"
												stroke-width="2"
												stroke-linecap="round"
												stroke-linejoin="round"
											/>
											<circle
												cx="12"
												cy="12"
												r="3"
												stroke="#999"
												stroke-width="2"
												stroke-linecap="round"
												stroke-linejoin="round"
											/>
										</svg>
										<svg
											width="18"
											height="18"
											viewBox="0 0 24 24"
											fill="none"
											v-else
										>
											<path
												d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"
												stroke="#999"
												stroke-width="2"
												stroke-linecap="round"
												stroke-linejoin="round"
											/>
										</svg>
									</span>
								</div>
								<div
									class="invalid-msg-txt"
									v-if="$v.rePassword.$error || checkpassowrdInvalid"
								>
									{{
										$v.rePassword.$dirty && !$v.rePassword.required
											? "Required"
											: checkpassowrdInvalid
											? passwordValidation
											: !$v.rePassword.maxLength && $v.rePassword.required
											? "Should not exceed 55 characters"
											: ""
									}}
								</div>
							</div>
							<div class="mb-4">
								<div class="position-relative">
									<b-form-input
										type="password"
										@paste="preventPaste"
										ref="pass"
										v-model="$v.reConfirmpassword.$model"
										@keypress.enter="resetPassword()"
										:class="{
											error: $v.reConfirmpassword.$error || match,
										}"
										placeholder="Confirm Password"
										class="login-input pr-5 single-field-update"
									></b-form-input>
								</div>
								<div
									class="invalid-msg-txt mt-1"
									v-if="$v.reConfirmpassword.$error || match"
								>
									{{
										$v.reConfirmpassword.$dirty &&
										!$v.reConfirmpassword.required
											? "Required"
											: !$v.reConfirmpassword.maxLength &&
											  $v.reConfirmpassword.required
											? "Should not exceed 55 characters"
											: match
											? "Password mismatch"
											: ""
									}}
								</div>
							</div>

							<b-button
								@click="resetPassword()"
								variant="primary"
								block
								class="login-submit-btn mb-4"
							>
								Reset Password
							</b-button>

							<div class="text-center mt-2">
								<a
									href="javascript:void(0)"
									@click="mode = 'login'"
									class="back-to-login font-inter fw-500 color-blue text-decoration-none"
								>
									<i class="fas fa-arrow-left mr-1"></i> Back to login
								</a>
							</div>
						</b-overlay>
					</div>
					<!-- Signup View -->
					<div v-if="mode === 'signup'" class="login-form-wrapper">
						<SignupForm
							:signinCheckData="signinCheckData"
							@next="handleSignupNext"
						/>
					</div>
					<Popupcomponent
						v-if="showPopup || registered"
						:showPopup="showPopup"
						:registered="registered"
						:candExists="candExists"
						:invalidText="invalidText"
						:successText="successText"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import loginMixin from "@/utils/mixins/loginMixin";
import Popupcomponent from "@/views/login/Popup.vue";
import SignupForm from "@/components/Onboarding/SignupForm.vue";
import intlTelInput from "intl-tel-input";

const customLoginMixin = { ...loginMixin };
// LoginContent is a child of LoginModal — it should NOT independently listen to EventBus
// open/close events or react to route changes. Those are handled by LoginModal.
// Removing these prevents duplicate listeners and watcher conflicts that cause modal flickering.
delete customLoginMixin.created;
delete customLoginMixin.mounted;
delete customLoginMixin.beforeDestroy;
// Strip mixin watchers that conflict with LoginContent's own watch block.
// Vue 2 merges mixin+component watchers, so without this the isOpen watcher runs twice
// (once emitting close-login-modal EventBus, once emitting @close to parent) causing a loop.
if (customLoginMixin.watch) {
	delete customLoginMixin.watch.$route;
	delete customLoginMixin.watch.isOpen;
}

export default {
	name: "LoginContent",
	mixins: [customLoginMixin],
	components: {
		Popupcomponent,
		SignupForm,
	},
	props: {
		isRoute: {
			type: Boolean,
			default: false,
		},
		hideBusinessTab: {
			type: Boolean,
			default: false,
		},
		visible: {
			type: Boolean,
			default: false,
		},
		initialMode: {
			type: String,
			default: "login",
		},
		signinCheckData: {
			type: Object,
			default: null,
		},
	},
	computed: {
		hasSetPasswordErrors() {
			return (
				(this.$v.setPassword && this.$v.setPassword.$error) ||
				this.checkpassowrdInvalid ||
				(this.$v.confirmPassword && this.$v.confirmPassword.$error) ||
				this.match ||
				(this.$v.otpCode && this.$v.otpCode.$error) ||
				this.invalidCredential
			);
		},
	},
	data() {
		return {
			itis: [],
			dialCodeLength: 2,
		};
	},
	created() {
		if (this.isRoute) {
			this.isOpen = true;
		}
		if (this.hideBusinessTab) {
			this.userType = "candidate";
		}
		if (this.initialMode === "signup") {
			this.mode = "signup";
		}
	},
	mounted() {
		this.startSlider();
		if (!this.useEmail && this.userType === "candidate") {
			this.initializeTel();
		}
	},
	beforeDestroy() {
		this.stopSlider();
		this.destroyTel();
		document.body.classList.remove("login-modal-open");
	},
	watch: {
		visible: {
			immediate: true,
			handler(val) {
				this.isOpen = val;
				if (val) {
					this.stopSlider();
					this.startSlider();
					document.body.classList.add("login-modal-open");
					if (!this.useEmail && this.userType === "candidate") {
						this.initializeTel();
					}
				} else {
					this.stopSlider();
					document.body.classList.remove("login-modal-open");
				}
			},
		},
		useEmail(val) {
			if (!val && this.userType === "candidate") {
				this.initializeTel();
			} else if (val) {
				this.destroyTel();
			}
		},
		userType(val) {
			if (val === "candidate" && !this.useEmail) {
				this.initializeTel();
			}
		},
		isOpen(val) {
			if (val) {
				this.stopSlider();
				this.startSlider();
				document.body.classList.add("login-modal-open");
			} else {
				this.stopSlider();
				this.$emit("close");
				this.destroyTel();
				document.body.classList.remove("login-modal-open");
			}
		},
	},
	methods: {
		startSlider() {
			this.stopSlider();
			this.sliderTimer = setInterval(() => {
				this.nextSlide();
			}, 5000);
		},
		handleSignupNext() {
			this.isOpen = false;
		},
		initializeTel() {
			this.$nextTick(() => {
				let inputs = [];
				const els = document.querySelectorAll("#candidatePhone");
				els.forEach((el) => {
					inputs.push(el);
				});

				if (inputs.length === 0) {
					let inputComponent = this.$refs.phoneInput;
					if (Array.isArray(inputComponent)) {
						inputComponent.forEach((c) => {
							if (c && c.$el) inputs.push(c.$el);
							else if (c) inputs.push(c);
						});
					} else if (inputComponent) {
						if (inputComponent.$el) inputs.push(inputComponent.$el);
						else inputs.push(inputComponent);
					}
				}

				if (this.itis && Array.isArray(this.itis)) {
					this.itis.forEach((iti) => iti.destroy());
				}
				this.itis = [];

				inputs.forEach((input) => {
					if (!input) return;
					const iti = intlTelInput(input, {
						utilsScript:
							"https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/19.5.3/js/utils.js",
						showSelectedDialCode: true,
						nationalMode: false,
						initialCountry: "IN",
						autoPlaceholder: "off",
					});
					this.itis.push(iti);

					const initialDialCode = iti.getSelectedCountryData().dialCode;
					this.dialCodeLength = initialDialCode.length;
					this.loginPhoneCountryCode = "+" + initialDialCode;

					const dropdown = input.parentElement.querySelector(
						".iti__flag-container"
					);
					if (dropdown) {
						dropdown.addEventListener("click", function () {
							const dropdownContainer = document.querySelector(
								".iti__dropdown-content"
							);
							if (dropdownContainer) {
								const searchField =
									dropdownContainer.querySelector(".iti__search-input");
								if (searchField) {
									searchField.value = "";
									const inputEvent = new Event("input", {
										bubbles: true,
									});
									searchField.dispatchEvent(inputEvent);
								}
							}
						});
					}

					input.addEventListener("countrychange", () => {
						const code = iti.getSelectedCountryData().dialCode;
						this.dialCodeLength = code.length;
						this.loginPhoneCountryCode = "+" + code;
						this.regForm.countryCode = "+" + code;

						const value = (this.loginPhone.number || "").replace(/\s+/g, "");
						const regValue = (this.regForm.phoneNumber || "").replace(
							/\s+/g,
							""
						);

						if (code !== "91" && value) {
							this.loginPhoneInvalid = !iti.isValidNumber();
						} else if (code === "91") {
							this.loginPhoneInvalid = value
								? !/^[6-9]\d{9}$/.test(value)
								: false;
						}

						if (
							this.regForm.countryCode !== "+91" &&
							this.regForm.phoneNumber
						) {
							this.invalidPhone = !iti.isValidNumber();
						} else if (this.regForm.countryCode === "+91") {
							this.invalidPhone = regValue
								? !/^[6-9]\d{9}$/.test(regValue)
								: false;
						}
					});

					input.addEventListener("input", () => {
						const code = iti.getSelectedCountryData().dialCode;
						const value = (this.loginPhone.number || "").replace(/\s+/g, "");
						const regValue = (this.regForm.phoneNumber || "").replace(
							/\s+/g,
							""
						);

						if (code !== "91" && value) {
							this.loginPhoneInvalid = !iti.isValidNumber();
						}
						if (this.regForm.countryCode !== "+91" && regValue) {
							this.invalidPhone = !iti.isValidNumber();
						}
					});
				});
			});
		},
		destroyTel() {
			if (this.itis && Array.isArray(this.itis)) {
				this.itis.forEach((iti) => iti.destroy());
				this.itis = [];
			}
		},
		// Overrides mixin handleEdit to also reinitialize phone input
		handleEdit() {
			this.setPasswordMode = false;
			this.showSetPasswordOtp = false;
			this.isResetPasswordMode = false;
			this.otpSent = false;
			this.otpCode = "";
			this.setPassword = "";
			this.confirmPassword = "";
			this.sendOtpError = "";
			this.userExists = null;
			this.hasPassword = null;
			this.useOtp = true;
			this.password = "";
			this.invalidCredential = false;
			this.invalidText = "";
			this.clearOtp();
			this.clearTimer();
			this.timerOut = false;
			this.otpExceeded = false;
			this.$v.$reset();
			// Re-initialize phone input after returning to step 1
			if (!this.useEmail && this.userType === "candidate") {
				this.$nextTick(() => {
					this.initializeTel();
				});
			}
		},
		// Resets back to step 1 (identifier entry) while keeping the email/phone pre-filled
		handleIdentifierEdit() {
			this.otpSent = false;
			this.otpCode = "";
			this.password = "";
			this.setPassword = "";
			this.confirmPassword = "";
			this.sendOtpError = "";
			this.setPasswordMode = false;
			this.showSetPasswordOtp = false;
			this.isResetPasswordMode = false;
			this.useOtp = true;
			this.invalidCredential = false;
			this.userExists = null;
			this.hasPassword = null;
			this.clearTimer();
			this.timerOut = false;
			this.$v.$reset();
			// Re-initialize phone input after returning to step 1 so the
			// country flag/dial-code widget is rendered correctly.
			if (!this.useEmail && this.userType === "candidate") {
				this.$nextTick(() => {
					this.initializeTel();
				});
			}
		},
	},
};
</script>

<style>
.overlaytxt {
	color: var(--gray-500, #667085);
	font-family: "Inter", sans-serif;
	font-size: 14px;
	font-style: normal;
	font-weight: 500;
	margin-top: 15px;
}

.overlaybox {
	height: 120px;
	width: 180px;
	background: #fff;
	border-radius: 16px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
}

.login-loader {
	color: #4a7eff;
}

.inputError {
	border: 1px solid#DC143C !important;
	border-radius: 25px !important;
	box-shadow: unset !important;
}

/* Class for error text */
.invalid-msg-txt {
	color: #dc143c;
	font-family: "Inter", sans-serif;
	font-size: 12px;
	font-style: normal;
	font-weight: 400;
}

.login-modal-body {
	border-radius: 40px !important;
	overflow: visible !important;
}

#loginModal .modal-content {
	border-radius: 40px !important;
	overflow: visible !important;
}

#loginModal .modal-dialog {
	max-width: 860px !important;
}

#loginModal .no-gutters {
	background-color: #ffffff;
	border-radius: 40px;
	overflow: hidden;
	height: 100%;
}

.right-panel {
	background-color: #ffffff;
	border-radius: 40px;
	z-index: 2;
	position: relative;
	left: -34px;
}

.login-modal-container {
	position: relative;
	background: #fff;
	height: 530px;
	max-height: calc(100vh - 60px);
	border-radius: 40px;
	font-family: system-ui !important;
}

.modal-bg-transparent {
	background: transparent !important;
}

.login-form-wrapper {
	padding: 20px 0px 20px 20px;
	margin-right: -10px;
}

.login-form-wrapper-2 {
	padding: 23px 0px 25px 25px;
}

.close-modal-btn {
	position: absolute;
	top: -1px;
	right: -43px;
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background: #fff;
	border: none;
	box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 18px;
	color: #666;
	z-index: 1000;
	cursor: pointer;
	transition: transform 0.2s ease;
}

.close-modal-btn:hover {
	transform: scale(1.1);
}

.left-panel {
	background-color: #041525;
	display: flex;
	flex-direction: column;
	position: relative;
	overflow: hidden;
	z-index: 1;
}

.left-panel::before {
	content: "";
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: radial-gradient(
		circle at 50% 50%,
		rgba(255, 255, 255, 0.05) 0%,
		transparent 70%
	);
	z-index: 2;
}

.carousel-title {
	font-size: 28px;
	line-height: 1.25;
	color: #ffffff;
	font-weight: 700;
	z-index: 10;
	position: relative;
	margin-top: 20px;
}

.illustration-placeholder {
	z-index: 5;
}

.slider-dots-container {
	z-index: 10;
	padding-bottom: 25px;
	position: absolute;
	width: 100%;
	bottom: -18px;
}

.dot {
	width: 6px;
	height: 6px;
	background-color: rgba(255, 255, 255, 0.5);
	border-radius: 50%;
	margin: 0 4px;
	cursor: pointer;
	transition: all 0.3s ease;
}

.dot.active {
	background-color: #fff;
	width: 7px;
	height: 7px;
}

#loginModal___BV_modal_backdrop_ {
	top: 0px !important;
}

/* Fade Transition */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 1s ease;
}

.fade-enter,
.fade-leave-to {
	opacity: 0;
}

.character-img {
	width: 100%;
	display: block;
	z-index: 2;
	position: relative;
}

.active-tab-label {
	height: 34px;
	margin-bottom: 16px;
	display: inline-block;
	position: relative;
	width: 100%;
	text-align: justify;
}

.active-tab-label-2 {
	margin-bottom: 22px;
	display: inline-block;
	position: relative;
	width: 100%;
	text-align: justify;
}

.active-tab-label::after {
	content: "";
	position: absolute;
	bottom: -2px;
	left: 18px;
	width: 58px;
	height: 2px;
	background-color: #20639b;
}

.active-tab-label-2::after {
	content: "";
	position: absolute;
	bottom: -2px;
	left: 18px;
	width: 68px;
	height: 2px;
	background-color: #20639b;
}

.color-blue {
	color: #20639b;
	font-size: 14px;
}

.toggle-container {
	background: #e2e8f0;
	border-radius: 32px;
	box-shadow: 0px 1px 2px 0px #0000000f;
	height: 48px;
	padding: 4px;
	display: flex;
	align-items: center;
}

/* #loginModal___BV_modal_outer_{
		z-index: 3000 !important;
} */
.toggle-btn {
	border-radius: 25px;
	border: none;
	background: transparent;
	padding: 0 20px;
	height: 40px;
	font-size: 14px;
	font-family: "Inter", sans-serif;
	font-weight: 600;
	color: #64748b;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	display: flex;
	align-items: center;
	justify-content: center;
}

.toggle-btn.active {
	color: #ffffff;
	background: linear-gradient(
		90deg,
		#20639b 0%,
		#2f4691 31.02%,
		#4e2f91 69.64%,
		#7c1585 131.69%
	);
	height: 40px;
}

.login-input {
	background-color: #fff !important;
	border: 1px solid #efefefee;
	border-radius: 25px !important;
	padding: 12px 20px !important;
	height: 48px !important;
	font-size: 15px;
	transition: all 0.2s ease;
}

.login-input:focus,
.phone-input:focus,
.single-field-update:focus,
.single-field-update:focus,
.single-field-update:focus,
.input-field-container .form-control:focus,
.phone-inputField .form-control:focus {
	border-color: #4a7eff !important;
	box-shadow: none !important;
	outline: none !important;
}

.password-input {
	padding-right: 40px !important;
}

#loginModal input:disabled:-webkit-autofill,
#loginModal input:disabled:-webkit-autofill:hover,
#loginModal input:disabled:-webkit-autofill:focus,
#loginModal input[disabled]:-webkit-autofill,
#loginModal input[disabled]:-webkit-autofill:hover,
#loginModal input[disabled]:-webkit-autofill:focus {
	-webkit-box-shadow: 0 0 0 1000px #f4f4f4 inset !important;
	box-shadow: 0 0 0 1000px #f4f4f4 inset !important;
	-webkit-text-fill-color: #64748b !important;
}

#loginModal input:-webkit-autofill,
#loginModal input:-webkit-autofill:hover,
#loginModal input:-webkit-autofill:focus {
	-webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
	box-shadow: 0 0 0 1000px #ffffff inset !important;
	-webkit-text-fill-color: #64748b !important;
}

.forgot-link {
	font-family: "Inter", sans-serif;
	font-size: 12px;
	color: #333;
	font-weight: 500;
	border-bottom: 1px solid #d7d7d7;
	color: #000000;
}

.forgot-link:hover {
	color: #333;
	text-decoration: none;
}

.login-submit-btn {
	background-color: #4a7eff !important;
	border: none !important;
	border-radius: 25px !important;
	height: 48px !important;
	font-weight: 600;
	font-size: 16px;
	transition: all 0.2s ease;
}

.register .login-submit-btn {
	height: 52px;
}

.login-submit-btn:not(:disabled):hover {
	background-color: #4a7eff !important;
	transform: translateY(-1px);
	box-shadow: none;
}

.login-submit-btn:focus,
.login-submit-btn:active,
.login-submit-btn:active:focus {
	box-shadow: none !important;
	outline: none !important;
}

.separator {
	position: relative;
	text-align: center;
}

.separator::before {
	content: "";
	position: absolute;
	top: 50%;
	left: 0;
	width: 100%;
	height: 1px;
	background: #eee;
}

.separator span {
	background: #fff;
	padding: 0 8px;
	font-family: "Inter", sans-serif;
	font-weight: 500;
	position: relative;
	color: #757575;
	font-size: 13px;
}

.google-login-btn {
	border: 1px solid #cbd5e1 !important;
	border-radius: 25px !important;
	height: 48px !important;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #fff !important;
	transition: all 0.2s ease;
	font-size: 14px;
	font-weight: 500;
	font-family: "Inter", sans-serif;
	color: #64748b !important;
	margin-top: 26px !important;
}

.google-login-btn:hover {
	background: #fcfcfc !important;
	border-color: #ccc !important;
}

.signup-text {
	font-weight: 500;
	font-size: 12px;
	color: #000000;
}

.register-link {
	color: #20639b;
	font-weight: 600;
	text-decoration: underline;
}

/* Registration specific styles */
.phone-input-group {
	overflow: hidden;
}

.country-code {
	font-size: 14px;
	font-weight: 500;
	color: #333;
}

.flag-icon-small {
	width: 20px;
	border-radius: 2px;
}

.divider-v {
	width: 1px;
	height: 20px;
	background-color: #efefef;
}

.no-shadow {
	box-shadow: none !important;
}

.password-toggle-icon {
	position: absolute;
	right: 15px;
	top: 19px;
	transform: translateY(-50%);
	cursor: pointer;
}

.terms-label {
	font-size: 11px;
	color: #666;
	line-height: 1.4;
}

.terms-label a {
	color: #20639b;
	text-decoration: underline;
}

.custom-checkbox-circle {
	transform: scale(0.9);
}

.custom-checkbox-circle .custom-control-label::before,
.custom-checkbox-circle
	.custom-control-input:focus
	~ .custom-control-label::before,
.custom-checkbox-circle
	.custom-control-input:active
	~ .custom-control-label::before,
.custom-checkbox-circle
	.custom-control-input:focus:not(:checked)
	~ .custom-control-label::before {
	box-shadow: none !important;
	outline: none !important;
}

.copyright-footer {
	font-family: "Inter", sans-serif;
	font-size: 10px;
	color: #999;
}

.fs-10 {
	font-size: 10px;
}

/* Error alert */
.error-alert {
	background: #fff0f0;
	border: 1px solid #ffcccc;
	border-radius: 10px;
	padding: 10px 16px;
	font-size: 13px;
	color: #cc0000;
	font-family: "Inter", sans-serif;
}

.inputError.login-input:focus {
	border-color: #dc143c !important;
	box-shadow: unset !important;
}

.inputError.phone-input {
	border-color: #dc143c !important;
	box-shadow: unset !important;
}
.inputError.phone-input:focus {
	border-color: #dc143c !important;
	box-shadow: unset !important;
}

/* Keep red border on focus when the field has a validation error */
.error.single-field-update:focus,
.error.login-input:focus,
.error.phone-input:focus,
.error.otp-box:focus,
.error.single-field-update:focus,
.error-border.single-field-update:focus,
.error-border.login-input:focus,
.input-field-container .form-control.error:focus,
.phone-inputField .form-control.error:focus {
	border-color: #dc143c !important;
	box-shadow: none !important;
}

.phone-input::placeholder {
	font-size: 14px !important;
	font-weight: 500 !important;
	font-family: "Inter", sans-serif !important;
	color: #64748b !important;
}

.login-input::placeholder {
	font-size: 14px !important;
	font-weight: 500 !important;
	font-family: "Inter", sans-serif !important;
	color: #acacac !important;
}

.otp-box::placeholder {
	font-size: 14px !important;
	font-weight: 500 !important;
	font-family: "Inter", sans-serif !important;
	color: #acacac !important;
}

/* OTP inputs */
.otp-input-row {
	gap: 10px;
}

.otp-box {
	flex: 1;
	width: 44px;
	height: 50px;
	border: 1.5px solid #efefef;
	border-radius: 25px;
	text-align: center;
	font-size: 20px;
	font-weight: 600;
	color: #333;
	outline: none;
	transition: border-color 0.2s ease;
}

.otp-box:focus {
	box-shadow: none;
	outline: none;
}

/* Change otp-box border only after a digit has been entered */
.otp-box:not(:placeholder-shown) {
	border-color: #4a7eff !important;
}

.input-field-container {
	position: relative;
}

/* Phone Number Design */
.input-field-container .iti {
	width: 100% !important;
}

.input-field-container .iti__selected-flag {
	border-right: none !important;
	border-bottom-left-radius: 32px !important;
	border-top-left-radius: 32px !important;
	background-color: transparent !important;
	position: relative;
	left: 12px;
}

.input-field-container .iti__selected-dial-code {
	color: #7a7a7a !important;
	font-weight: 600 !important;
	font-family: "Inter", sans-serif !important;
}

.phone-edit-action {
	position: absolute;
	right: 24px;
	top: 24px;
	transform: translateY(-50%);
	color: #4a7eff !important;
	font-size: 13px !important;
	font-family: "Inter", sans-serif !important;
	font-weight: 600 !important;
	cursor: pointer;
	z-index: 3;
}

.desktop-email-edit-btn {
	position: absolute;
	right: 24px;
	top: 50%;
	transform: translateY(-50%);
	color: #4a7eff !important;
	font-size: 13px !important;
	font-family: "Inter", sans-serif !important;
	font-weight: 600 !important;
	cursor: pointer;
	text-decoration: none !important;
	z-index: 3;
}

.desktop-email-edit-btn:hover {
	text-decoration: underline !important;
}

/* phone-input mirrors .login-input but deliberately omits padding-left
   so intlTelInput can inject the correct value for the flag+dialcode button */
.phone-input {
	background-color: #fff !important;
	border: 1px solid #e2e8f0 !important;
	border-radius: 32px !important;
	box-shadow: 0px 1px 2px 0px #0000000d !important;
	height: 48px !important;
	font-family: "Inter", sans-serif !important;
	font-weight: 500 !important;
	color: #64748b !important;
	opacity: 1 !important;
	padding-top: 12px !important;
	padding-right: 24px !important;
	padding-bottom: 12px !important;
	padding-left: 105px !important;
	font-size: 15px;
	transition: all 0.2s ease;
	box-sizing: border-box;
}

.phone-input:focus,
.iti--focus .phone-input {
	border-color: #4a7eff !important;
	outline: none;
}

.input-field-container
	.iti--inline-dropdown
	.iti__country-list::-webkit-scrollbar {
	width: 3px !important;
	background: #fff;
	border-radius: 20px;
}

.input-field-container
	.iti--inline-dropdown
	.iti__country-list::-webkit-scrollbar-thumb {
	background: #dfdfdf !important;
	border-radius: 20px;
	height: 20px !important;
}

.input-field-container
	.iti--inline-dropdown
	.iti__country-list::-webkit-scrollbar-thumb:hover {
	background: #dfdfdf !important;
}

.input-field-container .iti__search-input {
	color: #000;
	font-family: "Montserrat", sans-serif;
	font-size: 14px;
	font-weight: 400;
	line-height: 18.9px;
	height: 34px;
	padding: 7px 11px;
	border-radius: 8px;
	/* background: #e8eff4; */
}

.input-field-container .iti--inline-dropdown .iti__country-list {
	max-height: 185px;
	padding-right: 2px;
	margin-top: 3px;
	border-top: none !important;
}

.input-field-container .iti--inline-dropdown .iti__dropdown-content {
	position: absolute;
	z-index: 2;
	margin-top: 3px;
	margin-left: -1px;
	border: 1px solid #ccc;
	box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.2);
	padding: 5px;
}

.input-field-container .iti--show-selected-dial-code .iti__selected-flag {
	background: transparent !important;
	border-radius: 10px;
}

.no-number::-webkit-outer-spin-button,
.no-number::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

.no-number {
	appearance: textfield;
	-moz-appearance: textfield;
}

.register-overflow {
	padding-left: 6px;
	height: 420px;
	overflow-y: auto;
	overflow-x: hidden;
}

.login-overflow {
	height: 420px;
	max-height: calc(100vh - 160px);
	overflow: hidden !important;
}

.login-overflow.has-errors {
	overflow: auto !important;
}

.login-overflow::-webkit-scrollbar,
.register-overflow::-webkit-scrollbar {
	scroll-behavior: smooth !important;
	width: 0px !important;
	height: 0px !important;
	background-color: transparent !important;
}

.login-form-wrapper-2.reset,
.login-form-wrapper-2.forgot-pass,
.login-form-wrapper-2.forgotOtp {
	height: 560px;
}

@media (max-width: 1028px) {
	.login-form-wrapper-2.reset,
	.login-form-wrapper-2.forgot-pass,
	.login-form-wrapper-2.forgotOtp {
		height: calc(100vh - 160px);
	}

	.login-modal-container,
	.login-modal-body,
	#loginModal .modal-content,
	#loginModal .no-gutters {
		border-radius: 0 !important;
		height: calc(100vh - 155px);
		min-height: unset;
		overflow: auto;
	}

	.close-modal-btn {
		display: none;
	}

	.right-panel {
		background: #fff;
		left: 0;
	}

	#loginModal .modal-dialog {
		margin: 0;
	}

	.modal-open #loginModal {
		padding-left: 0 !important;
		overflow-y: auto;
	}

	#loginModal .modal-dialog-centered {
		min-height: unset;
		position: absolute;
		width: 100%;
		top: 0;
		align-items: flex-start;
		height: 100vh;
	}

	#loginModal .shadow-lg {
		box-shadow: none !important;
	}

	.active-tab-label,
	.active-tab-label-2 {
		margin-bottom: 12px;
	}

	.login-overflow,
	.register-overflow {
		height: calc(100vh - 250px);
	}
}

/* OTP inputs */
.otp-input-row {
	gap: 10px;
}

.otp-box {
	flex: 1;
	width: 44px;
	height: 50px;
	border: 1.5px solid #efefef;
	border-radius: 25px;
	text-align: center;
	font-size: 20px;
	font-weight: 600;
	color: #333;
	outline: none;
	transition: border-color 0.2s ease;
}

.otp-box:focus {
	border-color: #20639b;
	box-shadow: none;
}

.single-field-update {
	border-radius: 32px !important;
	border: 1px solid #e2e8f0 !important;
	box-shadow: 0px 1px 2px 0px #0000000d !important;
	height: 48px !important;
	color: #64748b !important;
	font-family: "Inter", sans-serif !important;
	font-weight: 500 !important;
	font-size: 14px !important;
	padding: 12px 24px !important;
}

.single-field-update::placeholder {
	color: #64748b !important;
	opacity: 1 !important;
}

.single-field-update:disabled,
.single-field-update[disabled],
.phone-inputField .form-control:disabled,
.phone-inputField .form-control[disabled],
.phone-input:disabled,
.phone-input[disabled] {
    background: #f4f4f4 !important;
    box-shadow: 0px 1px 2px 0px #0000000d !important;
    border: none !important;
    opacity: 1 !important;
    cursor: default !important;
}

.otp-timer-text {
	font-family: "Inter", sans-serif !important;
	font-size: 13px !important;
	font-weight: 400 !important;
	color: #4a7eff !important;
}

.otp-timer-text .time-bold {
	font-weight: 600 !important;
}

.single-field-update::placeholder {
	color: #64748b !important;
	opacity: 1 !important;
}

.set-password-container {
	display: flex !important;
	flex-direction: column !important;
	justify-content: space-between !important;
	min-height: 420px !important;
	overflow: auto;
}

.otp-sent-info-box {
	background: linear-gradient(
		90deg,
		rgba(74, 126, 255, 0.06) 0%,
		rgba(0, 38, 134, 0.06) 100%
	);
	border-radius: 40px !important;
	padding: 16px 24px !important;
}

.info-box-label {
	font-family: "Poppins", sans-serif !important;
	font-size: 14px !important;
	font-weight: 400 !important;
	color: #667085 !important;
}

.info-box-value {
	font-family: "Inter", sans-serif !important;
	font-size: 14px !important;
	font-weight: 600 !important;
	color: #1f1f1f !important;
}

.info-box-edit-link {
	font-family: "Inter", sans-serif !important;
	font-size: 13px !important;
	font-weight: 500 !important;
	color: #4a7eff !important;
	text-decoration: underline !important;
	cursor: pointer;
}

.forgot-password-link {
	color: #333333 !important;
	font-size: 13px !important;
	font-family: "Inter", sans-serif !important;
	font-weight: 500 !important;
	text-decoration: none !important;
	cursor: pointer;
}

.terms-privacy-text {
	font-family: "Manrope", sans-serif !important;
	color: #757575 !important;
	font-size: 13px !important;
	font-weight: 500 !important;
	line-height: 16px;
	margin: 0 auto !important;
}

.terms-privacy-text .terms-link {
	color: #4a7eff !important;
	text-decoration: underline !important;
	font-weight: 500 !important;
	cursor: pointer;
}

/* Phone Number Design */
.input-field-container .iti {
	width: 100% !important;
}

.phone-inputField .form-control {
	border-radius: 32px !important;
	border: 1px solid #e2e8f0 !important;
	box-shadow: 0px 1px 2px 0px #0000000d !important;
	height: 48px !important;
	font-family: "Inter", sans-serif !important;
	font-weight: 500 !important;
	color: #64748b !important;
	opacity: 1 !important;
	padding-right: 24px !important;
	padding-left: 105px !important;
}

.phone-inputField .form-control::placeholder {
	color: #64748b !important;
	opacity: 1 !important;
	font-size: 14px !important;
	font-weight: 500 !important;
	font-family: "Inter", sans-serif !important;
}

.phone-inputField .iti__selected-flag {
	border-right: none !important;
	border-bottom-left-radius: 32px !important;
	border-top-left-radius: 32px !important;
	background-color: transparent !important;
	padding-left: 24px !important;
	padding-right: 16px !important;
}

.phone-inputField label {
	font-family: "Inter", sans-serif !important;
	font-weight: 500 !important;
	color: #64748b !important;
	opacity: 1 !important;
	left: 105px !important;
}

.phone-inputField.focused label,
.phone-inputField input:focus + label,
.phone-inputField input:not(:placeholder-shown) + label {
	display: none !important;
}

.phone-inputField .iti__selected-dial-code {
	color: #7a7a7a !important;
	font-weight: 600 !important;
	font-family: "Inter", sans-serif !important;
}

.toggle-input-link {
	font-family: "Inter", sans-serif !important;
	font-weight: 600 !important;
	color: #4a7eff !important;
	text-decoration: none !important;
	font-size: 13px !important;
	cursor: pointer;
}

/* phone-input mirrors .login-input but deliberately omits padding-left
   so intlTelInput can inject the correct value for the flag+dialcode button */
.phone-input {
	background-color: #fff !important;
	border: 1px solid #e2e8f0 !important;
	border-radius: 32px !important;
	box-shadow: 0px 1px 2px 0px #0000000d !important;
	height: 48px !important;
	font-family: "Inter", sans-serif !important;
	font-weight: 500 !important;
	color: #64748b !important;
	opacity: 1 !important;
	padding-top: 12px !important;
	padding-right: 24px !important;
	padding-bottom: 12px !important;
	padding-left: 105px !important;
	font-size: 15px;
	transition: all 0.2s ease;
	box-sizing: border-box;
}

.phone-input:focus {
	border-color: #4a7eff !important;
	box-shadow: none !important;
	outline: none !important;
}

.input-field-container .iti--inline-dropdown .iti__dropdown-content {
	width: 485px !important;
}

.input-field-container
	.iti--inline-dropdown
	.iti__country-list::-webkit-scrollbar {
	width: 3px !important;
	background: #fff;
	border-radius: 20px;
}

.input-field-container
	.iti--inline-dropdown
	.iti__country-list::-webkit-scrollbar-thumb {
	background: #dfdfdf !important;
	border-radius: 20px;
	height: 20px !important;
}

.input-field-container
	.iti--inline-dropdown
	.iti__country-list::-webkit-scrollbar-thumb:hover {
	background: #dfdfdf !important;
}

.input-field-container .iti__search-input {
	color: #000;
	font-family: "Montserrat", sans-serif;
	font-size: 14px;
	font-weight: 400;
	line-height: 18.9px;
	height: 34px;
	padding: 7px 11px;
	border-radius: 8px;
	/* background: #e8eff4; */
}

.input-field-container .iti--inline-dropdown .iti__country-list {
	max-height: 185px;
	padding-right: 2px;
	margin-top: 3px;
	border-top: none !important;
}

.input-field-container .iti--inline-dropdown .iti__dropdown-content {
	position: absolute;
	z-index: 2;
	margin-top: 3px;
	margin-left: -1px;
	border: 1px solid #ccc;
	box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.2);
	padding: 5px;
}

.iti--inline-dropdown .iti__dropdown-content {
	border-radius: 11px;
}

.input-field-container .iti--show-selected-dial-code .iti__selected-flag {
	background: transparent !important;
	border-radius: 10px;
}

.no-number::-webkit-outer-spin-button,
.no-number::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

.no-number {
	appearance: textfield;
	-moz-appearance: textfield;
}

.register-overflow {
	padding-left: 6px;
	height: 420px;
	max-height: calc(100vh - 160px);
	overflow-y: auto;
	overflow-x: hidden;
}

.login-overflow {
	height: 420px;
	max-height: calc(100vh - 160px);
	overflow: hidden;
}

.login-overflow::-webkit-scrollbar,
.register-overflow::-webkit-scrollbar {
	scroll-behavior: smooth !important;
	width: 0px !important;
	height: 0px !important;
	background-color: transparent !important;
}

.login-form-wrapper-2.reset,
.login-form-wrapper-2.forgot-pass,
.login-form-wrapper-2.forgotOtp {
	height: 560px;
	max-height: calc(100vh - 60px);
	overflow-y: auto;
}

@media (max-width: 1028px) {
	/*
   * On mobile, b-modal is NOT used — LoginModalShell renders a plain fixed div.
   * All the old b-modal wrapper overrides (#loginModal .modal, .modal-dialog-centered,
   * #loginModal___BV_modal_backdrop_, etc.) are removed.
   * Positioning/height of the overlay itself is controlled in LoginModalShell.vue.
   */

	/* ── Container fills the overlay div ── */
	.login-modal-container {
		border-radius: 0;
		height: 100%;
		min-height: unset;
		overflow-y: auto;
	}

	.bussiness-login .login-modal-container {
		height: 100%;
	}

	.close-modal-btn {
		display: none;
	}

	.right-panel {
		background: #fff;
		left: 0;
		border-radius: 0;
	}

	.no-gutters {
		border-radius: 0;
		overflow: visible;
	}

	.login-form-wrapper {
		padding: 18px 0px;
	}

	.login-form-wrapper-2.reset,
	.login-form-wrapper-2.forgot-pass,
	.login-form-wrapper-2.forgotOtp {
		height: calc(100vh - 160px);
	}

	body.has-mobile-banner .login-form-wrapper-2.reset,
	body.has-mobile-banner .login-form-wrapper-2.forgot-pass,
	body.has-mobile-banner .login-form-wrapper-2.forgotOtp {
		height: calc(100vh - 266px);
	}

	.active-tab-label,
	.active-tab-label-2 {
		margin-bottom: 12px;
	}

	.login-overflow,
	.register-overflow {
		height: calc(100vh - 235px);
		padding: 0 18px;
		overflow: hidden auto;
	}

	body.has-mobile-banner .login-overflow,
	body.has-mobile-banner .register-overflow {
		height: calc(100vh - 295px);
		padding: 0 18px;
	}

	.bussiness-login .login-overflow {
		height: calc(100vh - 215px);
		padding: 0 18px;
	}

	body.has-mobile-banner .bussiness-login .login-overflow {
		height: calc(100vh - 209px);
		padding: 0 18px;
	}

	.bussiness-login .register-overflow {
		height: calc(100vh - 136px);
		padding: 0 18px;
	}

	body.has-mobile-banner .bussiness-login .register-overflow {
		height: calc(100vh - 200px);
		padding: 0 18px;
	}

	.bussiness-login .login-form-wrapper-2 {
		padding: 23px 25px 25px 25px;
	}

	.bussiness-login .login-form-wrapper-2.reset,
	.bussiness-login .login-form-wrapper-2.forgot-pass,
	.bussiness-login .login-form-wrapper-2.forgotOtp {
		height: calc(100vh - 72px);
	}

	body.has-mobile-banner .bussiness-login .login-form-wrapper-2.reset,
	body.has-mobile-banner .bussiness-login .login-form-wrapper-2.forgot-pass,
	body.has-mobile-banner .bussiness-login .login-form-wrapper-2.forgotOtp {
		height: calc(100vh - 136px);
		padding: 0 18px;
	}
}

/* Custom Mobile Login Screen Styles */
.mobile-login-screen {
	width: 100%;
	min-height: 100vh;
	background: linear-gradient(190.09deg, #f6f6f7 10.81%, #e4e4e4 371.3%);
	display: flex;
	flex-direction: column;
	padding-top:env(safe-area-inset-top)
}

.mobile-overlay-wrapper {
	flex-grow: 1 !important;
	display: flex !important;
	flex-direction: column !important;
	margin-top: -30px;
	position: relative;
	z-index: 2;
	border-top-left-radius: 40px !important;
	border-top-right-radius: 40px !important;
	overflow: hidden;
}

.mobile-header-panel {
	background-image: url("~@/assets/img/homepage/header-bg.svg");
	background-size: cover;
	background-position: center;
	background-repeat: no-repeat;
	padding: 30px 24px 60px 24px;
	position: relative;
}

.mobile-header-panel.business-header-active {
	background-image: url("~@/assets/img/homepage/business-header.svg") !important;
}

.header-back-wrapper {
	position: absolute;
	top: 30px;
	left: 20px;
}

.header-back-icon {
	width: 32px;
	height: 32px;
	cursor: pointer;
	display: block;
}

.business-header-active .header-back-icon {
	background: #7a7a7a !important;
	border-radius: 50% !important;
}

.back-btn {
	background: rgba(255, 255, 255, 0.1);
	border: none;
	border-radius: 50%;
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	padding: 0;
	transition: background 0.2s ease;
}

.back-btn:hover {
	background: rgba(255, 255, 255, 0.2);
}

.rotated-back-arrow {
	width: 32px;
	height: 32px;
}

.hire3x-round-logo {
	width: 60px;
	height: 60px;
}

.mobile-header-title {
	font-family: "Manrope", sans-serif !important;
	font-weight: 800 !important;
	font-size: 28px !important;
	line-height: 36px !important;
	color: #ffffff !important;
	margin-bottom: 12px !important;
}

.mobile-header-title.business-title {
	color: #2e425d !important;
}

.mobile-header-subtitle {
	font-family: "Inter", sans-serif !important;
	font-weight: 500 !important;
	font-size: 12px !important;
	color: #94a3b8 !important;
	margin: 0 !important;
}

@media (max-width: 1028px) {
	.input-field-container .iti__search-input {
		display: none !important;
	}
}

.mobile-form-panel {
	background: #ffffff !important;
	border-top-left-radius: 40px !important;
	border-top-right-radius: 40px !important;
	padding: 16px !important;
	flex-grow: 1;
	animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.mobile-form-panel.set-password-padding {
	padding: 24px 16px !important;
}

@keyframes slideUp {
	0% {
		transform: translateY(100%);
		opacity: 0.8;
	}

	100% {
		transform: translateY(0);
		opacity: 1;
	}
}

.mob-toggle-container {
	background: #e8edf5;
	border-radius: 30px;
	padding: 4px;
	height: 48px;
	display: flex;
	align-items: center;
}

.mob-toggle-track {
	display: flex !important;
	padding: 4px !important;
}

.mob-toggle-btn {
	border: none;
	background: transparent;
	padding: 0 20px;
	height: 40px;
	font-family: "Inter", sans-serif;
	font-size: 14px;
	font-weight: 600;
	color: #64748b;
	border-radius: 26px;
	transition: all 0.3s ease;
	display: flex;
	align-items: center;
	justify-content: center;
}

.mob-toggle-btn.active {
	background: linear-gradient(
		90deg,
		#20639b 0%,
		#2f4691 31.02%,
		#4e2f91 69.64%,
		#7c1585 131.69%
	) !important;
	color: #ffffff !important;
	height: 40px;
}

.mob-google-btn {
	background: #ffffff !important;
	border: 1px solid #cbd5e1 !important;
	border-radius: 32px !important;
	height: 48px !important;
	font-family: "Inter", sans-serif !important;
	font-weight: 500 !important;
	font-size: 14px !important;
	color: #64748b !important;
	display: flex !important;
	align-items: center !important;
	justify-content: center !important;
	box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05) !important;
}

.mob-separator {
	display: flex;
	align-items: center;
	text-align: center;
	color: #757575 !important;
	font-family: "Inter", sans-serif !important;
	font-size: 12px;
	font-weight: 500;
}

.mob-separator::before,
.mob-separator::after {
	content: "";
	flex: 1;
	border-bottom: 1px solid #cbd5e1 !important;
}

.mob-separator:not(:empty)::before {
	margin-right: 0.5em;
}

.mob-separator:not(:empty)::after {
	margin-left: 0.5em;
}

.single-field-update::placeholder {
	font-size: 14px !important;
	font-weight: 500 !important;
	font-family: "Inter", sans-serif !important;
}

.mob-toggle-link {
	font-family: "Inter", sans-serif !important;
	font-size: 12px !important;
	font-weight: 500 !important;
	color: #4a7eff !important;
	text-decoration: none !important;
}

.mob-continue-btn {
	background: #4a7eff !important;
	border: none !important;
	border-radius: 30px !important;
	height: 48px !important;
	font-family: "Inter", sans-serif !important;
	font-weight: 600 !important;
	font-size: 16px !important;
	color: #ffffff !important;
	box-shadow: 0px 4px 12px rgba(74, 126, 255, 0.24) !important;
}

.mob-terms-text {
	font-family: "Inter", sans-serif !important;
	font-size: 12px !important;
	color: #64748b !important;
	line-height: 18px !important;
}

.mob-terms-link {
	color: #4a7eff !important;
	text-decoration: underline !important;
}

.dummy-figma-box {
	background: #f4f4f4 !important;
	box-shadow: 0px 1px 2px 0px #0000000d !important;
	padding: 15px 20px !important;
	border-radius: 30px !important;
	display: flex !important;
	justify-content: space-between !important;
	align-items: center !important;
}

.dummy-email-text {
	font-family: "Inter", sans-serif !important;
	font-size: 14px !important;
	font-weight: 500 !important;
	color: #1f1f1f !important;
}

.dummy-edit-btn {
	font-family: "Inter", sans-serif !important;
	font-size: 13px !important;
	font-weight: 600 !important;
	color: #4a7eff !important;
	text-decoration: none !important;
}

.dummy-edit-btn:hover {
	text-decoration: underline !important;
}

.mob-email-edit-overlay {
	position: absolute !important;
	right: 16px !important;
	top: 50% !important;
	transform: translateY(-50%) !important;
	z-index: 3 !important;
	cursor: pointer !important;
}

.mobile-set-password-container {
	display: flex !important;
	flex-direction: column !important;
}

.set-password-title {
	font-family: "Poppins", sans-serif !important;
	font-size: 20px !important;
	font-weight: 600 !important;
	color: #1f1f1f !important;
	margin: 0 0 8px !important;
}

.set-password-subtitle {
	font-family: "Poppins", sans-serif !important;
	font-size: 14px !important;
	font-weight: 500 !important;
	color: #667085 !important;
	line-height: 20px !important;
}

.mob-sent-info-box {
	background: #f4f4f4 !important;
	box-shadow: 0px 1px 2px 0px #0000000d !important;
	padding: 15px 20px !important;
	border-radius: 30px !important;
	display: flex !important;
	justify-content: space-between !important;
	align-items: center !important;
}

.mob-sent-label {
	font-family: "Inter", sans-serif !important;
	font-size: 12px !important;
	font-weight: 400 !important;
	color: #667085 !important;
}

.mob-sent-email {
	font-family: "Inter", sans-serif !important;
	font-size: 14px !important;
	font-weight: 500 !important;
	color: #1f1f1f !important;
}

.mob-sent-edit-btn {
	font-family: "Inter", sans-serif !important;
	font-size: 12px !important;
	font-weight: 500 !important;
	color: #4a7eff !important;
	text-decoration: underline !important;
}

.mobile-fields-container {
	display: flex !important;
	flex-direction: column !important;
	gap: 24px !important;
}

.mob-resend-container {
	font-family: "Inter", sans-serif !important;
	font-size: 13px !important;
	color: #757575 !important;
	text-align: right !important;
	margin-top: -8px !important;
}

.mob-resend-link {
	color: #008000 !important;
	font-weight: 600 !important;
}

.dummy-complete-signup-container {
	display: flex !important;
	flex-direction: column !important;
}

.dummy-signup-banner {
	background: #e8f1ff !important;
	border-radius: 20px !important;
	padding: 16px 20px !important;
	position: relative !important;
	overflow: hidden !important;
}

.dummy-signup-title {
	font-family: "Poppins", sans-serif !important;
	font-size: 16px !important;
	font-weight: 600 !important;
	color: #1e40af !important;
	line-height: 22px !important;
	margin: 0 !important;
	text-align: left !important;
}

.dummy-banner-avatar {
	width: 55px !important;
	height: 55px !important;
	object-fit: contain !important;
}

.dummy-signup-fields-list {
	display: flex !important;
	flex-direction: column !important;
	gap: 16px !important;
}

.dummy-input-group {
	display: flex !important;
	flex-direction: column !important;
	align-items: flex-start !important;
}

.dummy-input-label {
	font-family: "Inter", sans-serif !important;
	font-size: 12px !important;
	font-weight: 500 !important;
	color: #475569 !important;
	margin-bottom: 6px !important;
}

.required-star {
	color: #ef4444 !important;
	margin-left: 2px !important;
}

.m-16px {
	margin-top: 16px !important;
}

.m-24px {
	margin-top: 24px !important;
}

@media (max-height: 650px) {
	.login-overflow {
		overflow: auto !important;
	}
}
.otp-links-container a {
	text-decoration: underline !important;
}
</style>
